import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, MessageSquare, Plus, X, Image, LayoutGrid, Lock, Film } from 'lucide-react';
import { apiService, FilterCondition, getApiErrorMessage, Index, MarkerInfo, SearchResult, Prompt, PromptRun } from '../services/api';
import type { SearchResultFullVideoState } from '../features/search-workspace/types';
import { useTabState } from '../contexts/TabStateContext';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureAccess } from '../contexts/FeatureAccessContext';
import { useTabActivity } from '../contexts/TabActivityContext';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { decodeCsv, decodeJsonFromBase64Url, encodeCsv, encodeJsonToBase64Url, normalizeSearchString, parseEnumParam, parseFlagParam } from '../navigation/urlCodec';
import { getImagePayloadWithStatus, removeImagePayload, storeImagePayload } from '../navigation/imagePayloadStore';
import SearchResultDetailModal from './SearchResultDetailModal';
import { getPromptSchemaDisplayFields } from './prompt-schema/schemaFields';
import SearchTileSizeControl from './search-workspace/SearchTileSizeControl';
import SearchScopeControls from './search-workspace/SearchScopeControls';
import SearchConditionEditor from './search-workspace/SearchConditionEditor';
import SearchQueryInput from './search-workspace/SearchQueryInput';
import SearchConfigurationPanel from './search-workspace/SearchConfigurationPanel';
import {
  applyFilters,
  canonicalizeFilterFieldName,
  extractFieldsFromResults,
  FieldDefinition,
  mergeFieldDefinitions,
} from '../utils/filterUtils';
import {
  MAX_CONDITIONS,
  canonicalizeOperator,
  validateConditions,
} from '../utils/conditionSearchFilters';
import {
  areConditionFiltersEqual,
  DEFAULT_CONDITION_FILTER,
  isConditionFilterSupported,
  normalizeConditionFilters,
  restoreConditionFilters,
} from '../utils/conditionSearchState';
import { AgenticChatThread } from './agentic-chat';
import SearchResultsGallery from './search-workspace/SearchResultsGallery';
import AgentResultsCanvas, { AgentResultsCanvasHandle } from './search-workspace/AgentResultsCanvas';
import { useBrowseGallery } from '../features/search-workspace/useBrowseGallery';
import { useSearchWorkspace } from '../contexts/SearchWorkspaceContext';
import { useAgenticSearchWorkspace } from '../contexts/AgenticSearchWorkspaceContext';
import LockedFeaturePanel from './LockedFeaturePanel';
import {
  buildSearchResultIdentitySignature,
  getSearchResultIdentity,
  getSearchResultKey as getResultKey,
  getSearchResultRunId as getResultRunId,
} from '../utils/searchResultIdentity';
import { isPromptRunSearchableStatus } from '../utils/promptRunProgress';
import { withSearchFilterMatches } from '../utils/searchFieldMatches';
import { DEFAULT_SQL_EDITOR_QUERY, SQL_PREVIEW_COLUMN_PREFIX } from '../features/sql-search/constants';
import { buildSqlSearchResultsFromExecuteResponse } from '../features/sql-search/rowMapping';
import {
  AsyncRequestOwnershipToken,
  buildAsyncRequestOwnerKey,
  isCurrentAsyncRequest,
} from '../utils/asyncRequestOwnership';
import './SearchTab.css';
import './AssistantPanel.css';

type LegacySearchMode = 'vector' | 'condition' | 'agentic' | 'sql';
type SearchMode = Exclude<LegacySearchMode, 'condition'>;
type DatasetStatus = 'idle' | 'loading' | 'success' | 'empty' | 'error';

const normalizeWorkspaceSearchMode = (mode: LegacySearchMode): SearchMode => (
  mode === 'agentic' ? 'agentic' : 'vector'
);

interface PromptRunLoadOwner {
  mode: SearchMode;
  token: AsyncRequestOwnershipToken;
}

const OWNED_KEYS = [
  'mode',
  'indexes',
  'runs',
  'q',
  'fields',
  'filters',
  'submitted',
  'imgToken',
  'detail',
  'session',
  'panel',
  'view'
];

const formatApiError = (err: any): string => {
  const backendError = err?.response?.data?.error;
  if ([
    'resource_quota_exceeded',
    'resource_rate_quota_exceeded',
    'llm_daily_budget_exceeded',
    'llm_budget_guard_open',
    'llm_billing_guard_unavailable',
  ].includes(String(backendError?.code || ''))) {
    return getApiErrorMessage(err, 'Search failed. Please try again.');
  }
  const backendMessage = backendError?.message;
  if (typeof backendMessage === 'string' && backendMessage.trim()) {
    const validationErrors = backendError?.details?.validation_errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      const detailText = validationErrors
        .map((error: any) => {
          const field = typeof error?.field === 'string' ? error.field : '';
          const message = typeof error?.message === 'string' ? error.message : '';
          return field && message ? `${field}: ${message}` : message || field;
        })
        .filter(Boolean)
        .slice(0, 3)
        .join('; ');
      return detailText ? `${backendMessage} (${detailText})` : backendMessage;
    }
    return backendMessage;
  }

  const fallback = err?.message;
  if (
    err?.code === 'ECONNABORTED'
    || (typeof fallback === 'string' && fallback.toLowerCase().includes('timeout'))
  ) {
    return 'Request timed out. Please narrow the scope or retry.';
  }
  if (!err?.response && err?.code === 'ERR_NETWORK') {
    return 'Network request failed (possible CORS or connectivity issue). Please retry or contact support.';
  }
  if (typeof fallback === 'string' && fallback.trim()) {
    return fallback;
  }
  return 'Search failed. Please try again.';
};

const isAbortedRequestError = (err: any): boolean => (
  err?.name === 'AbortError' || err?.code === 'ERR_CANCELED'
);

const MAX_MEDIA_SEARCH_IMAGE_BYTES = 8 * 1024 * 1024;
const SEARCH_IMAGE_HYDRATION_RETRY_DELAYS_MS = [350, 900, 1800];
const EMPTY_SEARCH_RESULTS: SearchResult[] = [];
const EMPTY_FILTERS: FilterCondition[] = [];
export const VECTOR_SEARCH_REQUIREMENTS_ERROR =
  'Please enter a search query or upload an image, and select at least one index';

export const isResultRepresentedInCanvas = (
  selected: SearchResult,
  results: SearchResult[],
  videoIds: ReadonlySet<string> = new Set(),
): boolean => {
  if (selected.result_type !== 'video') {
    const selectedIdentity = getSearchResultIdentity(selected);
    return results.some((candidate) => getSearchResultIdentity(candidate) === selectedIdentity);
  }
  if (videoIds.has(selected.video_id)) return true;
  const selectedRunId = getResultRunId(selected);
  return results.some((candidate) => (
    candidate.video_id === selected.video_id
    && (!selectedRunId || getResultRunId(candidate) === selectedRunId)
  ));
};

export const validateManualSearchConditions = ({
  filters,
  fieldDefinitions,
}: {
  filters: FilterCondition[];
  fieldDefinitions: FieldDefinition[];
}): { ok: boolean; message?: string } => {
  if (filters.length === 0) {
    return { ok: true };
  }
  return validateConditions(filters, fieldDefinitions);
};

export const shouldClearVectorSearchRequirementsError = ({
  error,
  query,
  hasSearchImage,
  selectedIndexes,
}: {
  error: string | null | undefined;
  query: string;
  hasSearchImage: boolean;
  selectedIndexes: string[] | null | undefined;
}): boolean => (
  error === VECTOR_SEARCH_REQUIREMENTS_ERROR
  && (query.trim().length > 0 || hasSearchImage)
  && Array.isArray(selectedIndexes)
  && selectedIndexes.length > 0
);

const hasMeaningfulFilter = (filter: FilterCondition): boolean => {
  if (filter.field?.trim()) return true;
  if (typeof filter.value === 'boolean') return true;
  if (typeof filter.value === 'number') return true;
  if (typeof filter.value === 'string' && filter.value.trim().length > 0) return true;
  return false;
};

type SearchDetailTokenV1 = {
  v: 1;
  result_id?: string;
  result_type?: SearchResult['result_type'];
  segment_id?: string;
  video_id?: string;
  run_id?: string | null;
  source_index_id?: string | null;
};

const areSearchFieldSelectionsEqual = (
  left: string[] | null,
  right: string[] | null,
): boolean => {
  if (left === right) {
    return true;
  }
  if (left === null || right === null) {
    return false;
  }
  if (left.length !== right.length) {
    return false;
  }
  return left.every((fieldName, index) => fieldName === right[index]);
};

const isPlainSchemaObject = (value: unknown): value is Record<string, any> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const getNestedObjectSchemaForFilters = (
  schema: unknown,
): Record<string, any> | null => {
  if (!isPlainSchemaObject(schema)) {
    return null;
  }

  if (schema.type === 'object') {
    return schema;
  }

  if (
    schema.type === 'array'
    && isPlainSchemaObject(schema.items)
    && schema.items.type === 'object'
  ) {
    return schema.items;
  }

  return null;
};

export const collectDynamicNestedFilterPrefixesFromSchema = (
  schema: unknown,
  parentSegments: string[] = [],
  prefixes: Set<string> = new Set(),
): Set<string> => {
  const objectSchema = getNestedObjectSchemaForFilters(schema);
  if (!objectSchema) {
    return prefixes;
  }

  if (objectSchema.additionalProperties !== false) {
    prefixes.add(parentSegments.join('.'));
  }

  const properties = isPlainSchemaObject(objectSchema.properties)
    ? objectSchema.properties
    : {};

  Object.entries(properties).forEach(([propertyName, propertySchema]) => {
    const propertySegments = [...parentSegments, propertyName];
    collectDynamicNestedFilterPrefixesFromSchema(
      propertySchema,
      propertySegments,
      prefixes,
    );
  });

  const nestedAdditionalPropertiesSchema = getNestedObjectSchemaForFilters(
    objectSchema.additionalProperties,
  );
  if (nestedAdditionalPropertiesSchema) {
    collectDynamicNestedFilterPrefixesFromSchema(
      nestedAdditionalPropertiesSchema,
      parentSegments,
      prefixes,
    );
  }

  return prefixes;
};

export const sanitizeSearchFieldSelection = (
  searchFields: string[] | null,
  searchableFieldNames: string[],
  options: {
    metadataComplete?: boolean;
    loadedFieldNames?: string[];
    loadedFieldDefinitions?: FieldDefinition[];
  } = {},
): string[] | null => {
  if (searchFields === null) {
    return null;
  }

  const metadataComplete = options.metadataComplete ?? true;
  const uniqueSelectedFieldNames = searchFields.filter(
    (fieldName, index) => fieldName.trim().length > 0 && searchFields.indexOf(fieldName) === index,
  );

  const allowedFieldNames = new Set(searchableFieldNames);
  const loadedFieldNames = new Set(options.loadedFieldNames || searchableFieldNames);
  const loadedFieldDefinitionByName = new Map(
    (options.loadedFieldDefinitions || []).map((field) => [field.name, field]),
  );

  if (!metadataComplete) {
    return uniqueSelectedFieldNames.filter(
      (fieldName) => {
        if (allowedFieldNames.has(fieldName) || !loadedFieldNames.has(fieldName)) {
          return true;
        }

        const loadedFieldDefinition = loadedFieldDefinitionByName.get(fieldName);
        return loadedFieldDefinition?.type === 'unknown';
      },
    );
  }

  if (searchableFieldNames.length === 0) {
    return uniqueSelectedFieldNames;
  }

  const sanitizedFieldNames = uniqueSelectedFieldNames.filter(
    (fieldName) => allowedFieldNames.has(fieldName),
  );

  if (sanitizedFieldNames.length === uniqueSelectedFieldNames.length) {
    return searchableFieldNames.length > 0 && sanitizedFieldNames.length === searchableFieldNames.length
      ? null
      : sanitizedFieldNames;
  }

  if (sanitizedFieldNames.length === 0) {
    return searchableFieldNames.length > 0 ? null : [];
  }

  return sanitizedFieldNames.length === searchableFieldNames.length
    ? null
    : sanitizedFieldNames;
};

type PromptRunVideoCountSource = Pick<PromptRun, 'total_videos' | 'completed_videos'> & {
  videos_completed?: number | null;
};

export const getPromptRunVideoCountLabel = (run: PromptRunVideoCountSource): string => {
  const rawCount = [run.total_videos, run.completed_videos, run.videos_completed]
    .find((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const count = Math.max(0, Math.floor(rawCount ?? 0));
  return `${count} video${count === 1 ? '' : 's'}`;
};

const getSearchScopeRunSecondaryText = (run: PromptRun): string => (
  `${new Date(run.executed_at).toLocaleDateString()} - ${
    isPromptRunSearchableStatus(run.status) ? getPromptRunVideoCountLabel(run) : run.status
  }`
);

export const isSqlQueryReplaceableByCatalogSuggestion = (
  currentQuery: string,
  catalogManagedQuery: string | null | undefined,
): boolean => {
  const normalizedCurrentQuery = currentQuery.trim();
  const normalizedCatalogManagedQuery = (catalogManagedQuery || '').trim();

  return (
    normalizedCurrentQuery === ''
    || normalizedCurrentQuery === DEFAULT_SQL_EDITOR_QUERY
    || isSqlDefaultCatalogQueryShape(normalizedCurrentQuery)
    || (
      normalizedCatalogManagedQuery.length > 0
      && normalizedCurrentQuery === normalizedCatalogManagedQuery
    )
  );
};

export const SQL_STALE_SCOPE_QUERY_ERROR =
  'SQL query does not reference a table in the current selected index/run scope. Use a scoped table from the current catalog or generate a new SQL query.';

export const SQL_CATALOG_LOADING_ERROR =
  'Loading scoped SQL catalog. Please retry in a moment.';

const SQL_DEFAULT_CATALOG_QUERY_SHAPE = /^SELECT\s+t\.video_name\s*,\s*t\.segment_start_time\s*,\s*t\.segment_end_time\s+FROM\s+"[^"]+"\s+AS\s+t\s+ORDER\s+BY\s+t\.segment_start_time\s+DESC\s+LIMIT\s+\d+\s*;?$/i;

export const isSqlDefaultCatalogQueryShape = (query: string): boolean => (
  SQL_DEFAULT_CATALOG_QUERY_SHAPE.test(query.trim())
  || query.includes('__sql_preview_')
);

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripSqlStringLiteralsAndComments = (query: string): string => (
  query
    .replace(/\/\*[\s\S]*?\*\//g, ' ')
    .replace(/--[^\r\n]*/g, ' ')
    .replace(/'(?:''|[^'])*'/g, "''")
);

const quoteSqlIdentifier = (identifier: string): string => `"${identifier.replace(/"/g, '""')}"`;

export const shouldUseSqlResultsForCurrentSelection = ({
  currentScopeSignature,
  resultScopeSignature,
}: {
  currentScopeSignature: string;
  resultScopeSignature: string | null | undefined;
}): boolean => resultScopeSignature === currentScopeSignature;

const SQL_DEFAULT_VISIBLE_COLUMNS = [
  'video_name',
  'segment_start_time',
  'segment_end_time',
] as const;

const SQL_DEFAULT_PREVIEW_COLUMNS = [
  'video_id',
  'segment_id',
  'run_id',
  'index_id',
  'segment_uri',
  'thumbnail_uri',
  'thumbnail_gcs_uri',
  'thumbnail_available',
  'gif_uri',
  'gif_gcs_uri',
  'gif_available',
] as const;

export const isSqlPreviewHelperColumn = (columnName: string): boolean => (
  columnName.trim().toLowerCase().startsWith(SQL_PREVIEW_COLUMN_PREFIX)
);

export const getVisibleSqlResultColumns = (columns: string[]): string[] => (
  columns.filter((columnName) => !isSqlPreviewHelperColumn(columnName))
);

export const buildSqlCatalogDefaultPreviewQuery = (
  table: { table_name: string; context_columns?: string[] } | null | undefined,
): string => {
  const tableName = table?.table_name?.trim();
  if (!tableName) {
    return DEFAULT_SQL_EDITOR_QUERY;
  }

  const rawContextColumns = table?.context_columns;
  const contextColumns = Array.isArray(rawContextColumns)
    ? new Set(rawContextColumns)
    : null;
  const hasContextColumn = (columnName: string): boolean => (
    contextColumns === null || contextColumns.has(columnName)
  );

  const visibleSelections = SQL_DEFAULT_VISIBLE_COLUMNS
    .filter(hasContextColumn)
    .map((columnName) => `t.${columnName} AS ${columnName}`);
  const previewSelections = SQL_DEFAULT_PREVIEW_COLUMNS
    .filter(hasContextColumn)
    .map((columnName) => `t.${columnName} AS ${SQL_PREVIEW_COLUMN_PREFIX}${columnName}`);
  const selections = [...visibleSelections, ...previewSelections];
  const orderByClause = hasContextColumn('segment_start_time')
    ? 'ORDER BY t.segment_start_time DESC '
    : '';

  return (
    `SELECT ${selections.length > 0 ? selections.join(', ') : '1 AS ok'} `
    + `FROM ${quoteSqlIdentifier(tableName)} AS t `
    + orderByClause
    + 'LIMIT 200'
  );
};

const queryReferencesSqlTable = (query: string, tableName: string): boolean => {
  const normalizedTableName = tableName.trim();
  if (!normalizedTableName) {
    return false;
  }

  const searchableQuery = stripSqlStringLiteralsAndComments(query);
  const quotedIdentifier = quoteSqlIdentifier(normalizedTableName);
  if (searchableQuery.toLowerCase().includes(quotedIdentifier.toLowerCase())) {
    return true;
  }

  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(normalizedTableName)) {
    return false;
  }

  return new RegExp(`(^|[^A-Za-z0-9_$])${escapeRegExp(normalizedTableName)}([^A-Za-z0-9_$]|$)`, 'i')
    .test(searchableQuery);
};

export const doesSqlQueryReferenceAnyCatalogTable = (
  query: string,
  tableNames: string[],
): boolean => (
  tableNames.some((tableName) => queryReferencesSqlTable(query, tableName))
);

export type SqlScopedExecutionResolution =
  | {
      ok: true;
      query: string;
      adoptedCatalogQuery: boolean;
      catalogManagedQuery: string | null;
    }
  | {
      ok: false;
      error: string;
    };

export const resolveSqlQueryForScopedExecution = ({
  query,
  catalog,
  catalogManagedQuery,
}: {
  query: string;
  catalog: { tables?: Array<{ table_name: string; default_query?: string }> } | null | undefined;
  catalogManagedQuery?: string | null;
}): SqlScopedExecutionResolution => {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    return { ok: false, error: 'Please enter a SQL query' };
  }

  const tables = Array.isArray(catalog?.tables) ? catalog!.tables : [];
  const tableNames = tables
    .map((table) => table.table_name)
    .filter((tableName): tableName is string => typeof tableName === 'string' && tableName.trim().length > 0);

  if (tableNames.length === 0) {
    return { ok: false, error: SQL_CATALOG_LOADING_ERROR };
  }

  if (doesSqlQueryReferenceAnyCatalogTable(normalizedQuery, tableNames)) {
    return {
      ok: true,
      query: normalizedQuery,
      adoptedCatalogQuery: false,
      catalogManagedQuery: catalogManagedQuery || null,
    };
  }

  const suggestedQuery = buildSqlCatalogDefaultPreviewQuery(tables[0]).trim();
  if (
    suggestedQuery
    && isSqlQueryReplaceableByCatalogSuggestion(normalizedQuery, catalogManagedQuery)
  ) {
    return {
      ok: true,
      query: suggestedQuery,
      adoptedCatalogQuery: true,
      catalogManagedQuery: suggestedQuery,
    };
  }

  return { ok: false, error: SQL_STALE_SCOPE_QUERY_ERROR };
};

const encodeDetailToken = (result: SearchResult): string => {
  const payload: SearchDetailTokenV1 = {
    v: 1,
    result_id: result.result_id,
    result_type: result.result_type,
    segment_id: result.segment_id || undefined,
    video_id: result.video_id || undefined,
    run_id: getResultRunId(result),
    source_index_id: result.source_index_id ?? null,
  };
  return encodeJsonToBase64Url(payload);
};

const SearchTab: React.FC = () => {
  const { searchTabState, setSearchTabState } = useTabState();
  const { currentUser } = useAuth();
  const { getLockedFeature } = useFeatureAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const routerNavigationType = useNavigationType();
  const { isActive, navigationType: retainedNavigationType } = useTabActivity();
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  // Frozen Routes report POP. Use the retained real action when URL content
  // changes, without treating an ordinary tab return as fresh URL hydration.
  const navigationTypeRef = useRef(retainedNavigationType ?? routerNavigationType);
  navigationTypeRef.current = retainedNavigationType ?? routerNavigationType;
  const locationRef = useRef(location);
  locationRef.current = location;
  const {
    view: searchView,
    setView: setSearchView,
    setPanel: setSearchPanel,
    searchCanvas: manualCanvasKind,
    setSearchCanvas: setManualCanvasKind,
    getScrollPosition,
    setScrollPosition,
    primeSearchRuntimeCatalog,
  } = useSearchWorkspace();
  const { controller: agenticController, activate: activateAgenticWorkspace } = useAgenticSearchWorkspace();
  const agentResultsCanvasRef = useRef<AgentResultsCanvasHandle>(null);
  const resultsCanvasRef = useRef<HTMLElement>(null);
  const resetResultsCanvasScroll = useCallback(() => {
    if (resultsCanvasRef.current) resultsCanvasRef.current.scrollTop = 0;
  }, []);
  const browsePaginationSentinelRef = useRef<HTMLDivElement>(null);
  const [urlStateResolved, setUrlStateResolved] = useState(false);
  const [urlHydrated, setUrlHydrated] = useState(false);
  const [bootstrapStatus, setBootstrapStatus] = useState<DatasetStatus>('idle');
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const historyModeRef = useRef<HistoryMode>('replace');
  const isApplyingUrlRef = useRef(false);
  const lastCommittedSearchRef = useRef('');
  const executeSearchRef = useRef<() => Promise<boolean>>(async () => false);
  const pendingSubmittedRef = useRef(false);
  const hasHydratedOnceRef = useRef(false);
  const [submittedReplayToken, setSubmittedReplayToken] = useState(0);
  const pendingDetailRef = useRef<string | null>(null);
  const pendingSessionRef = useRef<string | null>(null);
  const activeSearchModeRef = useRef<SearchMode>(normalizeWorkspaceSearchMode(searchTabState.activeMode));
  const currentConditionScopeSignatureRef = useRef('');
  const currentSqlScopeSignatureRef = useRef('');
  const currentConditionResultSignatureRef = useRef('');
  const conditionSearchAbortRef = useRef<AbortController | null>(null);
  const conditionLoadMoreAbortRef = useRef<AbortController | null>(null);
  const sqlCatalogAbortRef = useRef<AbortController | null>(null);
  const sqlSearchAbortRef = useRef<AbortController | null>(null);
  const sqlSearchRequestSeqRef = useRef(0);
  const vectorSearchAbortRef = useRef<AbortController | null>(null);
  const vectorSearchRequestSeqRef = useRef(0);
  const conditionSearchRequestSeqRef = useRef(0);
  const conditionLoadMoreRequestSeqRef = useRef(0);
  const previousSqlScopeSignatureRef = useRef<string | null>(null);
  const conditionCursorContextRef = useRef<{
    cursor: string | null;
    scopeSignature: string;
    resultSignature: string;
  }>({
    cursor: null,
    scopeSignature: '',
    resultSignature: '',
  });

  const [indexes, setIndexes] = useState<Index[]>([]);
  const [isLoadingIndexes, setIsLoadingIndexes] = useState(true);
  const [hasLoadedIndexes, setHasLoadedIndexes] = useState(false);
  const bootstrapGenerationRef = useRef(0);
  const promptRunLoadGenerationRef = useRef(0);
  const promptRunLoadOwnerKeyRef = useRef('');
  const [runCatalogStatusByIndex, setRunCatalogStatusByIndex] = useState<Record<string, DatasetStatus>>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Use selected search result from context for persistence across tab switches
  const selectedSearchResult = searchTabState.selectedSearchResult;
  const [selectedAgentOccurrenceKey, setSelectedAgentOccurrenceKey] = useState<string | null>(null);
  const setSelectedSearchResult = useCallback((result: SearchResult | null) => {
    setSearchTabState(prev => ({
      ...prev,
      selectedSearchResult: result,
      selectedResultFullVideoState: result
        && prev.selectedResultFullVideoState?.resultIdentity !== getResultKey(result)
        ? null
        : prev.selectedResultFullVideoState,
    }));
  }, [setSearchTabState]);
  const selectedSearchResultKey = selectedSearchResult ? getResultKey(selectedSearchResult) : null;
  const selectedDetailToken = selectedSearchResult ? encodeDetailToken(selectedSearchResult) : null;

  const selectedResultFullVideoState = searchTabState.selectedResultFullVideoState;
  const setSelectedResultFullVideoState = useCallback((state: SearchResultFullVideoState | null) => {
    setSearchTabState(prev => {
      if (state && prev.selectedSearchResult
        && state.resultIdentity !== getResultKey(prev.selectedSearchResult)) return prev;
      return { ...prev, selectedResultFullVideoState: state };
    });
  }, [setSearchTabState]);

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('searchSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });

  // Get the current search mode from context
  // `condition` is accepted only as a persisted/URL compatibility token. The
  // unified Search panel executes condition-only queries while in `vector`.
  const searchMode = normalizeWorkspaceSearchMode(searchTabState.activeMode);

  // Separate loading states for each mode
  const [vectorIsLoading, setVectorIsLoading] = useState(false);
  const [sqlIsLoading, setSqlIsLoading] = useState(false);
  const [vectorDatasetStatus, setVectorDatasetStatus] = useState<DatasetStatus>('idle');
  const [conditionDatasetStatus, setConditionDatasetStatus] = useState<DatasetStatus>('idle');
  const [sqlDatasetStatus, setSqlDatasetStatus] = useState<DatasetStatus>('idle');

  // Use the appropriate loading state based on current mode
  const isLoading = searchMode === 'vector'
    ? vectorIsLoading
    : sqlIsLoading;
  const setIsLoading = searchMode === 'vector'
    ? setVectorIsLoading
    : setSqlIsLoading;
  // Separate index runs and prompts for each mode
  const [indexRuns, setIndexRuns] = useState<Record<string, PromptRun[]>>(
    {},
  );
  const [vectorRunPrompts, setVectorRunPrompts] = useState<Record<string, Prompt>>(
    {},
  );

  // Run identity/membership is workspace scope, so either panel's completed
  // fetch must be immediately reusable by the other panel. Prompt schemas
  // remain Search-only and are never loaded just for Agent.
  const runPrompts = vectorRunPrompts;

  // Get current mode state with proper typing
  const isSqlMode = searchMode === 'sql';
  const isAgenticMode = searchMode === 'agentic';
  const isConditionLikeMode = !isAgenticMode;
  const agenticLockedFeature = getLockedFeature('agentic_search');
  const isAgenticLocked = !!agenticLockedFeature;
  const currentState = isAgenticMode
    ? searchTabState.agenticSearch
    : searchTabState.vectorSearch;

  // Common properties across all modes
  const {
    error: stateError,
    showIndexDropdown,
    indexSortBy,
    indexSortAsc,
    showAllIndexes,
    showRunDropdown = false,
  } = currentState;

  // Extract selectedIndexes with fallback for compatibility
  const selectedIndexes: string[] | null = currentState.selectedIndexes ?? null;
  // Helper: first selected index for API calls that require a path parameter
  const firstSelectedIndex = selectedIndexes && selectedIndexes.length > 0 ? selectedIndexes[0] : '';
  const sqlState = searchTabState.sqlSearch;
  const sqlQuery = sqlState.query;
  const sqlCatalog = sqlState.catalog;
  const sqlCatalogScopeSignature = sqlState.catalogScopeSignature;
  const sqlCatalogManagedQuery = sqlState.catalogManagedQuery;
  const sqlResults = sqlState.results;
  const sqlSearchResults = sqlState.searchResults || EMPTY_SEARCH_RESULTS;
  const sqlError = sqlState.error;
  const sqlHasSearched = sqlState.hasSearched;
  const [sqlCatalogIsLoading, setSqlCatalogIsLoading] = useState(false);
  const sqlAutoQueryRef = useRef<string>(sqlCatalogManagedQuery || DEFAULT_SQL_EDITOR_QUERY);
  const sqlQueryRef = useRef<string>(sqlState.query);

  const updateSqlState = useCallback((
    updates:
      | Partial<typeof sqlState>
      | ((previousSqlState: typeof sqlState) => Partial<typeof sqlState>),
  ) => {
    setSearchTabState((prev) => {
      const resolvedUpdates = typeof updates === 'function'
        ? updates(prev.sqlSearch)
        : updates;
      return {
        ...prev,
        sqlSearch: {
          ...prev.sqlSearch,
          ...resolvedUpdates,
        },
      };
    });
  }, [setSearchTabState]);

  const showPromptDetails = isAgenticMode ? false : (currentState as any).showPromptDetails || false;
  const selectedRunIds: string[] | null = ((currentState as any).selectedRunIds ?? null);
  const selectedRunIdsForUrl: string[] | null = isAgenticMode
    ? (searchTabState.agenticSearch.selectedRunIds ?? null)
    : selectedRunIds;
  const selectedConditionLikeRunsLoaded = useMemo(
    () => (
      !isConditionLikeMode
      || !selectedIndexes
      || selectedIndexes.length === 0
      || selectedIndexes.every((indexId) => Array.isArray(indexRuns[indexId]))
    ),
    [indexRuns, isConditionLikeMode, selectedIndexes],
  );
  const selectedConditionLikeRunIdsForRequests: string[] | null = useMemo(() => {
    if (!isConditionLikeMode) {
      return selectedRunIds;
    }
    if (selectedRunIds === null) {
      return null;
    }
    if (!selectedConditionLikeRunsLoaded || !selectedIndexes || selectedIndexes.length === 0) {
      // Avoid forwarding stale run ids while selected-index runs are still loading.
      return null;
    }
    const availableRunIds = new Set(
      selectedIndexes.flatMap((indexId) => (indexRuns[indexId] || []).map((run) => run.run_id)),
    );
    return selectedRunIds.filter((runId) => availableRunIds.has(runId));
  }, [
    indexRuns,
    isConditionLikeMode,
    selectedConditionLikeRunsLoaded,
    selectedIndexes,
    selectedRunIds,
  ]);
  const hasExplicitEmptySqlRunSelection = !isAgenticMode
    && Array.isArray(selectedRunIds)
    && selectedRunIds.length === 0;
  const hasSearched = isAgenticMode
    ? false
    : isSqlMode
      ? sqlHasSearched
      : searchTabState.vectorSearch.hasSearched;
  const sqlScopeSignature = useMemo(() => JSON.stringify({
    indexes: [...(selectedIndexes || [])].sort(),
    runs: [...(selectedRunIds || [])].sort(),
  }), [
    selectedIndexes,
    selectedRunIds,
  ]);
  const isSqlCatalogCurrent = Boolean(
    sqlCatalog
    && sqlCatalogScopeSignature === sqlScopeSignature
  );
  const activeSqlCatalog = isSqlCatalogCurrent ? sqlCatalog : null;
  const isSqlResultsCurrent = Boolean(
    sqlResults
    && shouldUseSqlResultsForCurrentSelection({
      currentScopeSignature: sqlScopeSignature,
      resultScopeSignature: sqlState.scopeSignature,
    })
  );
  const activeSqlSearchResults = isSqlResultsCurrent ? sqlSearchResults : EMPTY_SEARCH_RESULTS;

  // Properties specific to vector/condition search
  const searchResults: SearchResult[] = isAgenticMode
    ? EMPTY_SEARCH_RESULTS
    : isSqlMode
      ? activeSqlSearchResults
      : searchTabState.vectorSearch.searchResults || EMPTY_SEARCH_RESULTS;

  // Get mode-specific values
  const query = searchTabState.vectorSearch.query;
  const filters: FilterCondition[] = !isAgenticMode
    ? ((searchTabState.vectorSearch.filters || EMPTY_FILTERS) as FilterCondition[])
    : EMPTY_FILTERS;
  const searchFields = searchTabState.vectorSearch.searchFields ?? null;
  // Agentic search specific values
  const persistedAgenticState = searchTabState.agenticSearch;
  const {
    state: agenticStoreState,
    activeDetail: agenticActiveDetail,
    activeThreadMessages: agenticThreadMessages,
    isProcessing: isAgenticProcessing,
    activeStatusMessage: agenticStatusMessage,
    loadSessions: loadAgenticSessions,
    openSession: openAgenticSession,
    startNewSessionDraft: startAgenticSessionDraft,
    sendMessage: sendAgenticMessage,
    deleteSession: deleteAgenticSession,
    selectResultSet: selectAgenticResultSet,
    patchActiveResults: patchAgenticActiveResults,
  } = agenticController;
  const activeAgenticLiveTurn = agenticStoreState.activeTurn
    && agenticStoreState.activeTurn.sessionId === agenticStoreState.activeSessionId
      ? agenticStoreState.activeTurn
      : null;
  const agenticProcessingSessionId = agenticStoreState.activeTurn?.sessionId ?? null;
  const userSessions = agenticStoreState.sessionSummaries;
  const isLoadingSessions = agenticStoreState.syncStatus.sessions === 'loading' && userSessions.length === 0;

  const error = isAgenticMode ? agenticStoreState.error : stateError;
  const effectiveError = isAgenticMode ? agenticStoreState.error : (isSqlMode ? sqlError : error);

  useEffect(() => {
    if (manualCanvasKind === 'sql') {
      setManualCanvasKind(searchTabState.vectorSearch.hasSearched ? 'search' : 'browse');
    } else if (manualCanvasKind === 'search' && !searchTabState.vectorSearch.hasSearched) {
      setManualCanvasKind('browse');
    }
  }, [
    manualCanvasKind,
    searchTabState.vectorSearch.hasSearched,
    setManualCanvasKind,
  ]);

  useEffect(() => {
    sqlQueryRef.current = sqlQuery;
  }, [sqlQuery]);

  useEffect(() => {
    if (sqlCatalogManagedQuery) {
      sqlAutoQueryRef.current = sqlCatalogManagedQuery;
    }
  }, [sqlCatalogManagedQuery]);

  // Filter dropdown states

  // Search fields dropdown state
  const [showSearchFieldsDropdown, setShowSearchFieldsDropdown] = useState(false);

  // Image search state (token persisted in tab state)
  const searchImageToken = searchTabState.vectorSearch.searchImageToken ?? null;
  const [searchImage, setSearchImage] = useState<string | null>(null);
  const nextPageToken = (
    searchMode === 'vector'
    && manualCanvasKind === 'search'
    && !query.trim()
    && !searchImage
  ) ? searchTabState.conditionSearch.nextPageToken : null;
  const [hydratedSearchImageToken, setHydratedSearchImageToken] = useState<string | null>(null);
  const [isResolvingSearchImage, setIsResolvingSearchImage] = useState(false);
  const imageUploadRequestRef = useRef(0);
  const imageHydrationRequestRef = useRef(0);
  const searchImageTokenRef = useRef<string | null>(searchImageToken);
  useEffect(() => () => {
    imageUploadRequestRef.current += 1;
    imageHydrationRequestRef.current += 1;
  }, []);
  useEffect(() => {
    searchImageTokenRef.current = searchImageToken;
  }, [searchImageToken]);
  useEffect(() => {
    let cancelled = false;

    const loadSearchImage = async () => {
      const hydrationRequestId = ++imageHydrationRequestRef.current;
      const finishResolvingIfCurrent = () => {
        if (imageHydrationRequestRef.current === hydrationRequestId) {
          setIsResolvingSearchImage(false);
        }
      };

      try {
        if (!searchImageToken) {
          setSearchImage(null);
          setHydratedSearchImageToken(null);
          finishResolvingIfCurrent();
          return;
        }

        const hydrationUploadVersion = imageUploadRequestRef.current;
        const isHydrationInvalid = () =>
          cancelled || hydrationUploadVersion !== imageUploadRequestRef.current;
        setIsResolvingSearchImage(true);
        let lookup = await getImagePayloadWithStatus(
          searchImageToken,
          currentUser?.uid,
        );

        // IndexedDB can be temporarily unavailable during upgrade/block fallbacks.
        // Retry unavailable lookups before treating token hydration as a miss.
        for (const retryDelayMs of SEARCH_IMAGE_HYDRATION_RETRY_DELAYS_MS) {
          if (lookup.status !== 'unavailable') break;
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, retryDelayMs);
          });
          if (isHydrationInvalid()) {
            finishResolvingIfCurrent();
            return;
          }
          lookup = await getImagePayloadWithStatus(
            searchImageToken,
            currentUser?.uid,
          );
        }

        if (isHydrationInvalid()) {
          finishResolvingIfCurrent();
          return;
        }

        const payload = lookup.payload;
        setSearchImage(payload);
        setHydratedSearchImageToken(
          lookup.status === 'found' ? searchImageToken : null
        );
        finishResolvingIfCurrent();

        if (payload) return;
        if (lookup.status === 'unavailable') {
          setUrlNotice('Search image is temporarily unavailable in this tab. Retry in a moment or reload.');
          return;
        }

        const tokenFromUrl = new URLSearchParams(location.search).get('imgToken');
        if (tokenFromUrl === searchImageToken) {
          setUrlNotice('Search image from URL is no longer available. Please upload the image again.');
        }

        setSearchTabState((prev) => {
          if (prev.vectorSearch.searchImageToken !== searchImageToken) {
            return prev;
          }
          return {
            ...prev,
            vectorSearch: {
              ...prev.vectorSearch,
              searchImageToken: null,
            },
          };
        });
      } catch {
        if (cancelled) return;
        setSearchImage(null);
        setHydratedSearchImageToken(null);
        finishResolvingIfCurrent();
      }
    };

    void loadSearchImage();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, location.search, searchImageToken, setSearchTabState]);
  const searchImagePreviewSrc = useMemo(() => {
    if (!searchImage) return null;
    const mime = searchImage.startsWith('/9j/')
      ? 'image/jpeg'
      : searchImage.startsWith('iVBORw0KGgo')
        ? 'image/png'
        : searchImage.startsWith('R0lGOD')
          ? 'image/gif'
          : searchImage.startsWith('UklGR')
            ? 'image/webp'
            : 'image/png';
    return `data:${mime};base64,${searchImage}`;
  }, [searchImage]);

  useEffect(() => {
    if (searchMode !== 'vector') {
      return;
    }

    if (!shouldClearVectorSearchRequirementsError({
      error: stateError,
      query,
      hasSearchImage: Boolean(searchImage),
      selectedIndexes,
    })) {
      return;
    }

    setSearchTabState((prev) => {
      if (!shouldClearVectorSearchRequirementsError({
        error: prev.vectorSearch.error,
        query: prev.vectorSearch.query,
        hasSearchImage: Boolean(searchImage),
        selectedIndexes: prev.vectorSearch.selectedIndexes,
      })) {
        return prev;
      }

      return {
        ...prev,
        vectorSearch: {
          ...prev.vectorSearch,
          error: null,
        },
      };
    });
  }, [query, searchImage, searchMode, selectedIndexes, setSearchTabState, stateError]);

  const imageInputRef = useRef<HTMLInputElement>(null);

  const rawAgenticAllResults = agenticActiveDetail?.all_results || EMPTY_SEARCH_RESULTS;
  const agenticDisplayMessages = agenticThreadMessages;
  const allAgenticResults = rawAgenticAllResults;

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!isActive) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Close search fields dropdown when clicking outside
      if (!target.closest('.search-fields-container')) {
        setShowSearchFieldsDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isActive]);

  // Helper function to update state
  const updateState = useCallback((updates: any) => {
    setSearchTabState(prev => {
      if (searchMode === 'vector') {
        return {
          ...prev,
          vectorSearch: { ...prev.vectorSearch, ...updates }
        };
      } else if (searchMode === 'sql') {
        return {
          ...prev,
          conditionSearch: { ...prev.conditionSearch, ...updates }
        };
      } else {
        return {
          ...prev,
          agenticSearch: { ...prev.agenticSearch, ...updates }
        };
      }
    });
  }, [searchMode, setSearchTabState]);
  const updateManualState = useCallback((updates: any) => {
    setSearchTabState((previous) => ({
      ...previous,
      vectorSearch: { ...previous.vectorSearch, ...updates },
    }));
  }, [setSearchTabState]);
  const vectorRunPromptsRef = useRef(vectorRunPrompts);
  vectorRunPromptsRef.current = vectorRunPrompts;
  const selectedIndexRunLoadSignature = JSON.stringify(
    [...(selectedIndexes || [])].sort(),
  );
  const selectedIndexIdsForRunLoading = useMemo<string[]>(
    () => JSON.parse(selectedIndexRunLoadSignature),
    [selectedIndexRunLoadSignature],
  );

  useEffect(() => {
    activeSearchModeRef.current = searchMode;
  }, [searchMode]);

  const clearSelectedResultState = useCallback(() => {
    setSearchTabState((prev) => {
      if (
        prev.selectedSearchResult === null
        && prev.selectedResultFullVideoState === null
      ) {
        return prev;
      }

      return {
        ...prev,
        selectedSearchResult: null,
        selectedResultFullVideoState: null,
      };
    });
  }, [setSearchTabState]);

  const cancelOutstandingConditionRequests = useCallback(() => {
    conditionSearchAbortRef.current?.abort();
    conditionSearchAbortRef.current = null;
    conditionLoadMoreAbortRef.current?.abort();
    conditionLoadMoreAbortRef.current = null;
  }, []);

  const cancelOutstandingVectorRequest = useCallback(() => {
    vectorSearchAbortRef.current?.abort();
    vectorSearchAbortRef.current = null;
  }, []);

  useEffect(() => () => {
    vectorSearchRequestSeqRef.current += 1;
    cancelOutstandingVectorRequest();
  }, [cancelOutstandingVectorRequest]);

  const cancelOutstandingSqlRequests = useCallback(() => {
    sqlCatalogAbortRef.current?.abort();
    sqlCatalogAbortRef.current = null;
    sqlSearchAbortRef.current?.abort();
    sqlSearchAbortRef.current = null;
  }, []);

  const cancelOutstandingSqlExecutionRequests = useCallback(() => {
    sqlSearchAbortRef.current?.abort();
    sqlSearchAbortRef.current = null;
  }, []);

  const applySharedWorkspaceScope = useCallback(({
    indexIds,
    runIds,
  }: {
    indexIds: string[];
    runIds: string[] | null;
  }) => {
    if (indexIds.length === 0) {
      return;
    }
    const normalizedIndexes = indexIds.slice(0, 1);
    const indexChanged = firstSelectedIndex !== normalizedIndexes[0];
    if (indexChanged) {
      const nextIndexId = normalizedIndexes[0];
      setIndexRuns((previous) => {
        const next = { ...previous };
        delete next[nextIndexId];
        return next;
      });
      setRunCatalogStatusByIndex((previous) => ({ ...previous, [nextIndexId]: 'loading' }));
    }
    setManualCanvasKind('browse');
    vectorSearchRequestSeqRef.current += 1;
    conditionSearchRequestSeqRef.current += 1;
    conditionLoadMoreRequestSeqRef.current += 1;
    sqlSearchRequestSeqRef.current += 1;
    cancelOutstandingVectorRequest();
    cancelOutstandingConditionRequests();
    cancelOutstandingSqlRequests();
    setVectorIsLoading(false);
    setSqlIsLoading(false);
    setSqlCatalogIsLoading(false);
    setVectorDatasetStatus('idle');
    setConditionDatasetStatus('idle');
    setSqlDatasetStatus('idle');
    setIsLoadingMore(false);
    setSearchTabState((previous) => {
      return {
        ...previous,
        selectedSearchResult: null,
        selectedResultFullVideoState: null,
        vectorSearch: {
          ...previous.vectorSearch,
          selectedIndexes: normalizedIndexes,
          selectedRunIds: runIds,
          ...(indexChanged ? { searchFields: null } : {}),
          searchResults: [],
          hasSearched: false,
          error: null,
        },
        conditionSearch: {
          ...previous.conditionSearch,
          selectedIndexes: normalizedIndexes,
          selectedRunIds: runIds,
          searchResults: [],
          hasSearched: false,
          nextPageToken: null,
          nextCursor: null,
          error: null,
        },
        agenticSearch: {
          ...previous.agenticSearch,
          selectedIndexes: normalizedIndexes,
          selectedRunIds: runIds,
          error: null,
        },
        sqlSearch: {
          ...previous.sqlSearch,
          catalog: null,
          catalogScopeSignature: null,
          results: null,
          searchResults: [],
          hasSearched: false,
          scopeSignature: null,
          error: null,
        },
      };
    });
  }, [
    cancelOutstandingConditionRequests,
    cancelOutstandingSqlRequests,
    cancelOutstandingVectorRequest,
    firstSelectedIndex,
    setManualCanvasKind,
    setSearchTabState,
  ]);

  // Helper to set search mode
  const setSearchMode = (mode: SearchMode) => {
    historyModeRef.current = 'push';
    activeSearchModeRef.current = mode;
    if (mode !== searchMode) {
      vectorSearchRequestSeqRef.current += 1;
      conditionSearchRequestSeqRef.current += 1;
      conditionLoadMoreRequestSeqRef.current += 1;
      sqlSearchRequestSeqRef.current += 1;
      cancelOutstandingVectorRequest();
      cancelOutstandingConditionRequests();
      cancelOutstandingSqlRequests();
      setVectorDatasetStatus('idle');
      setConditionDatasetStatus('idle');
      setSqlDatasetStatus('idle');
      setManualCanvasKind('browse');
    }
    if (mode !== 'vector') {
      vectorSearchRequestSeqRef.current += 1;
      cancelOutstandingVectorRequest();
      setVectorIsLoading(false);
    }
    if (searchMode === 'sql' && mode !== 'sql') {
      cancelOutstandingSqlRequests();
      setSqlIsLoading(false);
      setSqlCatalogIsLoading(false);
    }
    updateSqlState({ error: null });
    // Clear any error when switching modes
    setSearchTabState(prev => {
      const modeToStateKey = (modeValue: SearchMode) => {
        if (modeValue === 'vector') return 'vectorSearch';
        if (modeValue === 'agentic') return 'agenticSearch';
        return 'conditionSearch';
      };
      const sourceKey = prev.activeMode === 'vector'
        ? 'vectorSearch'
        : prev.activeMode === 'condition' || prev.activeMode === 'sql'
          ? 'conditionSearch'
          : 'agenticSearch';
      const targetKey = modeToStateKey(mode);
      const sourceState = prev[sourceKey] as any;
      const targetState = prev[targetKey] as any;
      // Index/run selection is one workspace scope, not mode-local state.
      // Vector owns the canonical scope because it is also the Search panel's
      // browse/manual source; every panel transition mirrors that scope.
      const sharedIndexes = (prev.vectorSearch.selectedIndexes?.length
        ? prev.vectorSearch.selectedIndexes
        : sourceState.selectedIndexes) ?? null;
      const sharedRunIds = prev.vectorSearch.selectedRunIds ?? sourceState.selectedRunIds ?? null;

      const nextState: typeof prev = {
        ...prev,
        activeMode: mode,
        selectedSearchResult: null,
        selectedResultFullVideoState: null,
        [targetKey]: {
          ...targetState,
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          error: null,
        },
        vectorSearch: {
          ...prev.vectorSearch,
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          ...(mode !== searchMode ? { searchResults: [], hasSearched: false } : {}),
        },
        agenticSearch: {
          ...prev.agenticSearch,
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          error: mode === 'agentic' ? null : prev.agenticSearch.error,
        },
        sqlSearch: mode !== searchMode
          ? {
              ...prev.sqlSearch,
              results: null,
              searchResults: [],
              hasSearched: false,
              scopeSignature: null,
              error: null,
            }
          : prev.sqlSearch,
      };
      if (targetKey === 'conditionSearch') {
        nextState.conditionSearch = {
          ...nextState.conditionSearch,
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          error: null,
          ...(mode !== searchMode ? {
            searchResults: [],
            hasSearched: false,
            nextPageToken: null,
            nextCursor: null,
          } : {}),
        };
      }
      return nextState;
    });
  };

  useEffect(() => {
    const navigationType = navigationTypeRef.current;
    const normalizedLocationSearch = normalizeSearchString(location.search);
    const isSelfAuthoredNavigation = hasHydratedOnceRef.current
      && navigationType !== 'POP'
      && normalizedLocationSearch === lastCommittedSearchRef.current;

    if (isSelfAuthoredNavigation) {
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
      return;
    }

    setUrlHydrated(false);
    setUrlStateResolved(false);
    setBootstrapStatus('loading');
    isApplyingUrlRef.current = true;
    const params = new URLSearchParams(location.search);
    // Only override mode if explicitly specified in URL - otherwise preserve current state
    const hasModeParam = params.has('mode');
    const hasIndexesParam = params.has('indexes');
    const hasRunsParam = params.has('runs');
    const hasQueryParam = params.has('q');
    const hasFieldsParam = params.has('fields');
    const hasFiltersParam = params.has('filters');
    const hasSubmittedParam = params.has('submitted');
    const hasDetailParam = params.has('detail');
    const hasSessionParam = params.has('session');
    const hasViewParam = params.has('view');
    const panelParam = parseEnumParam(params.get('panel'), ['search', 'agent'] as const, 'search');
    const viewParam = parseEnumParam(params.get('view'), ['segments', 'videos'] as const, 'segments');

    const urlModeParam = params.get('mode');
    const urlMode = urlModeParam
      ? parseEnumParam(urlModeParam, ['vector', 'condition', 'agentic', 'sql'] as const, 'vector')
      : null; // null means "preserve current mode"
    const urlIndexes = decodeCsv(params.get('indexes'));
    const urlRuns = decodeCsv(params.get('runs'));
    const urlQuery = params.get('q') || '';
    const urlFields = decodeCsv(params.get('fields'));
    const urlFilters = decodeJsonFromBase64Url<FilterCondition[]>(params.get('filters'));
    const submitted = hasSubmittedParam ? parseFlagParam(params.get('submitted')) : false;
    const detailParam = hasDetailParam ? (params.get('detail') || null) : null;
    const urlSessionId = params.get('session') || null;
    const imageToken = params.get('imgToken');
    const hasOwnedSearchParams = OWNED_KEYS
      .filter((key) => key !== 'imgToken')
      .some((key) => params.has(key));
    // Preserve persisted image only for bare `/search` navigations (e.g., tab switch).
    // For explicit search URLs that omit imgToken, clear stale image state.
    const shouldOverrideImageTokenFromUrl = imageToken !== null || hasOwnedSearchParams;
    const nextVectorImageToken = shouldOverrideImageTokenFromUrl ? imageToken : undefined;

    // Replay submitted searches only for initial hydration or browser navigation,
    // not for local replace/push URL updates while editing inputs.
    const requestedRemovedSqlMode = urlModeParam === 'sql';
    const shouldReplaySubmittedSearch = hasSubmittedParam
      && submitted
      && !requestedRemovedSqlMode
      && (!hasHydratedOnceRef.current || navigationType === 'POP');
    pendingSubmittedRef.current = shouldReplaySubmittedSearch;
    if (shouldReplaySubmittedSearch) {
      setSubmittedReplayToken((prev) => prev + 1);
    }
    pendingDetailRef.current = detailParam;
    // Handle pending session - check explicit URL mode or preserved mode
    const effectiveMode: SearchMode = params.has('panel')
      ? (panelParam === 'agent' ? 'agentic' : 'vector')
      : hasModeParam
        ? urlMode === 'agentic'
          ? 'agentic'
          : 'vector'
        : normalizeWorkspaceSearchMode(activeSearchModeRef.current);
    pendingSessionRef.current = effectiveMode === 'agentic' && hasSessionParam ? urlSessionId : null;
    setSearchPanel(effectiveMode === 'agentic' ? 'agent' : 'search');
    // A bare /search is an application-tab return, not a request to reset the
    // provider-owned canvas preference. Explicit/shared URLs still override it.
    if (hasViewParam) {
      setSearchView(viewParam);
    }
    if (effectiveMode === 'agentic' && !isAgenticLocked) {
      activateAgenticWorkspace();
    }
    if (hasIndexesParam || hasRunsParam) {
      setManualCanvasKind('browse');
    }

    setSearchTabState((prev) => {
      const normalizedUrlFilters = normalizeConditionFilters(urlFilters);
      const supportedUrlFilters = normalizedUrlFilters.every(isConditionFilterSupported)
        ? normalizedUrlFilters
        : [];
      const parsedVectorFilters = supportedUrlFilters;
      const parsedConditionFilters = supportedUrlFilters.length > 0
        ? supportedUrlFilters
        : restoreConditionFilters(urlFilters);
      const parsedIndexes = urlIndexes.length > 0 ? urlIndexes.slice(0, 1) : null;
      const parsedRuns = urlRuns.length > 0 ? urlRuns : null;
      const parsedFields = urlFields.length > 0 ? urlFields : null;
      const hydratedMode = params.has('panel') || hasModeParam
        ? effectiveMode
        : normalizeWorkspaceSearchMode(prev.activeMode);
      const sharedIndexes = hasIndexesParam
        ? parsedIndexes
        : prev.vectorSearch.selectedIndexes?.length
          ? prev.vectorSearch.selectedIndexes.slice(0, 1)
          : prev.agenticSearch.selectedIndexes?.length
            ? prev.agenticSearch.selectedIndexes.slice(0, 1)
            : null;
      const sharedRunIds = hasRunsParam
        ? parsedRuns
        : prev.vectorSearch.selectedRunIds ?? prev.agenticSearch.selectedRunIds ?? null;
      const scopeChanged = (
        (prev.vectorSearch.selectedIndexes?.[0] || null) !== (sharedIndexes?.[0] || null)
        || JSON.stringify(prev.vectorSearch.selectedRunIds) !== JSON.stringify(sharedRunIds)
      );

      return {
        ...prev,
        // Preserve current mode if not explicitly set in URL
        activeMode: hydratedMode,
        selectedSearchResult: scopeChanged
          ? null
          : hasDetailParam
          ? (detailParam ? prev.selectedSearchResult : null)
          : prev.selectedSearchResult,
        ...(scopeChanged ? {
          selectedResultFullVideoState: null,
        } : {}),
        vectorSearch: {
          ...prev.vectorSearch,
          query: hasQueryParam && !requestedRemovedSqlMode ? urlQuery : prev.vectorSearch.query,
          ...(nextVectorImageToken !== undefined ? { searchImageToken: nextVectorImageToken } : {}),
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          searchFields: hasFieldsParam ? parsedFields : prev.vectorSearch.searchFields,
          filters: hasFiltersParam ? parsedVectorFilters : prev.vectorSearch.filters,
          searchResults: scopeChanged ? [] : prev.vectorSearch.searchResults,
          hasSearched: hasSubmittedParam && !requestedRemovedSqlMode
            ? submitted
            : scopeChanged
              ? false
              : prev.vectorSearch.hasSearched,
          error: hasOwnedSearchParams ? null : prev.vectorSearch.error,
        },
        conditionSearch: {
          ...prev.conditionSearch,
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          filters: hasFiltersParam ? parsedConditionFilters : prev.conditionSearch.filters,
          searchResults: scopeChanged ? [] : prev.conditionSearch.searchResults,
          hasSearched: hasSubmittedParam ? submitted : scopeChanged ? false : prev.conditionSearch.hasSearched,
          nextPageToken: scopeChanged ? null : prev.conditionSearch.nextPageToken,
          nextCursor: scopeChanged ? null : prev.conditionSearch.nextCursor,
          error: hasOwnedSearchParams ? null : prev.conditionSearch.error,
        },
        agenticSearch: {
          ...prev.agenticSearch,
          selectedIndexes: sharedIndexes,
          selectedRunIds: sharedRunIds,
          sessionId: hasSessionParam ? (urlSessionId || null) : prev.agenticSearch.sessionId,
          // Clear messages when no session in URL to avoid orphaned transcripts
          ...(
            hasSessionParam
              ? (urlSessionId ? {} : { messages: [], suggestedSegments: [] })
              : {}
          ),
          error: hasOwnedSearchParams ? null : prev.agenticSearch.error,
        },
        sqlSearch: {
          ...prev.sqlSearch,
          query: hasQueryParam && hydratedMode === 'sql' ? urlQuery : prev.sqlSearch.query,
          ...(scopeChanged ? {
            catalog: null,
            catalogScopeSignature: null,
            results: null,
            searchResults: [],
            hasSearched: false,
            scopeSignature: null,
          } : {}),
          error: hasOwnedSearchParams ? null : prev.sqlSearch.error,
        },
      };
    });

    const timer = window.setTimeout(() => {
      isApplyingUrlRef.current = false;
      setUrlStateResolved(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [
    activateAgenticWorkspace,
    isAgenticLocked,
    location.search,
    setSearchPanel,
    setManualCanvasKind,
    setSearchView,
    setSearchTabState,
  ]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    if (!isActive) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.custom-select-container')) {
        updateState({ showIndexDropdown: false, showRunDropdown: false });
      }
    };

    if (showIndexDropdown || showRunDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, showIndexDropdown, showRunDropdown, updateState]);

  useEffect(() => {
    const hasOpenMenu = showIndexDropdown
      || showRunDropdown
      || showSearchFieldsDropdown;
    if (!isActive || !hasOpenMenu) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setShowSearchFieldsDropdown(false);
      updateState({ showIndexDropdown: false, showRunDropdown: false });
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [
    isActive,
    showIndexDropdown,
    showRunDropdown,
    showSearchFieldsDropdown,
    updateState,
  ]);

  const loadPromptRuns = useCallback(async (
    indexId: string,
    owner: PromptRunLoadOwner,
  ) => {
    const isCurrentOwner = () => isCurrentAsyncRequest(
      promptRunLoadGenerationRef.current,
      promptRunLoadOwnerKeyRef.current,
      owner.token,
    );

    try {
      setRunCatalogStatusByIndex((previous) => ({ ...previous, [indexId]: 'loading' }));
      const runs = (await apiService.getPromptRunsByIndex(indexId, 'searchable'))
        .filter((run) => isPromptRunSearchableStatus(run.status));
      if (!isCurrentOwner()) {
        return;
      }
      const resolvedRuns = runs || [];
      setRunCatalogStatusByIndex((previous) => ({
        ...previous,
        [indexId]: resolvedRuns.length > 0 ? 'success' : 'empty',
      }));

      setIndexRuns((previous) => (
        previous[indexId] === resolvedRuns
          ? previous
          : { ...previous, [indexId]: resolvedRuns }
      ));
      primeSearchRuntimeCatalog({
        runsByIndex: { [indexId]: resolvedRuns },
      });

      // Agentic search only needs run identity/status. Condition/SQL/vector
      // prompt schemas are loaded once per unique prompt and in parallel.
      if (owner.mode === 'agentic') return;
      const missingPromptIds = Array.from(new Set(
        resolvedRuns
          .map((run) => run.prompt_id)
          .filter((promptId): promptId is string => (
            Boolean(promptId) && !vectorRunPromptsRef.current[promptId]
          )),
      ));
      const loadedPrompts = await Promise.all(missingPromptIds.map(async (promptId) => {
        try {
          return [promptId, await apiService.getPrompt(promptId)] as const;
        } catch (err) {
          if (isCurrentOwner()) {
            console.error('Error loading prompt for run:', err);
          }
          return null;
        }
      }));
      if (!isCurrentOwner()) return;
      const resolvedPromptEntries = loadedPrompts.filter((
        entry,
      ): entry is readonly [string, Prompt] => entry !== null);
      setVectorRunPrompts((previous) => {
        const next = { ...previous };
        resolvedPromptEntries.forEach(([promptId, prompt]) => {
          if (!next[promptId]) next[promptId] = prompt;
        });
        vectorRunPromptsRef.current = next;
        return next;
      });
      if (resolvedPromptEntries.length > 0) {
        primeSearchRuntimeCatalog({
          promptsById: Object.fromEntries(resolvedPromptEntries),
        });
      }
    } catch (err) {
      if (!isCurrentOwner()) {
        return;
      }
      console.error('Error loading prompt runs:', err);
      setRunCatalogStatusByIndex((previous) => ({ ...previous, [indexId]: 'error' }));
    }
  }, [primeSearchRuntimeCatalog]);

  useEffect(() => {
    if (!isSqlMode) {
      return;
    }

    if (!selectedIndexes || selectedIndexes.length === 0 || !firstSelectedIndex) {
      updateSqlState({
        catalog: null,
        catalogScopeSignature: null,
        results: null,
        searchResults: [],
        hasSearched: false,
        error: null,
        scopeSignature: null,
      });
      return;
    }

    if (!selectedConditionLikeRunsLoaded) {
      return;
    }

    if (hasExplicitEmptySqlRunSelection) {
      updateSqlState({
        catalog: null,
        catalogScopeSignature: null,
        results: null,
        searchResults: [],
        hasSearched: false,
        error: null,
        scopeSignature: null,
      });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const requestScopeSignature = sqlScopeSignature;
    sqlCatalogAbortRef.current?.abort();
    sqlCatalogAbortRef.current = controller;

    const loadCatalog = async () => {
      setSqlCatalogIsLoading(true);
      try {
        const payload = await apiService.getSqlSearchCatalog(
          firstSelectedIndex,
          {
            runIds: selectedConditionLikeRunIdsForRequests,
            indexIds: selectedIndexes,
          },
          controller.signal,
        );

        if (cancelled || controller.signal.aborted || activeSearchModeRef.current !== 'sql') {
          return;
        }

        updateSqlState((previousSqlState) => {
          const shouldResetResults = previousSqlState.scopeSignature !== requestScopeSignature;
          const suggestedQuery = buildSqlCatalogDefaultPreviewQuery(payload.tables[0]).trim()
            || DEFAULT_SQL_EDITOR_QUERY;
          const shouldAdoptSuggestedQuery = payload.tables.length > 0 && isSqlQueryReplaceableByCatalogSuggestion(
            previousSqlState.query,
            previousSqlState.catalogManagedQuery || sqlAutoQueryRef.current,
          );

          if (shouldAdoptSuggestedQuery) {
            sqlAutoQueryRef.current = suggestedQuery;
            sqlQueryRef.current = suggestedQuery;
          }

          return {
            catalog: payload,
            catalogScopeSignature: requestScopeSignature,
            error: null,
            ...(shouldAdoptSuggestedQuery
              ? {
                  query: suggestedQuery,
                  catalogManagedQuery: suggestedQuery,
                }
              : {}),
            ...(shouldResetResults
              ? {
                  results: null,
                  searchResults: [],
                  hasSearched: false,
                  scopeSignature: null,
                }
              : {}),
          };
        });
      } catch (err: any) {
        if (isAbortedRequestError(err) || cancelled || controller.signal.aborted) {
          return;
        }
        updateSqlState({
          catalog: null,
          catalogScopeSignature: null,
          error: formatApiError(err),
        });
      } finally {
        if (!cancelled) {
          setSqlCatalogIsLoading(false);
        }
      }
    };

    void loadCatalog();

    return () => {
      cancelled = true;
      controller.abort();
      if (sqlCatalogAbortRef.current === controller) {
        sqlCatalogAbortRef.current = null;
      }
    };
  }, [
    firstSelectedIndex,
    hasExplicitEmptySqlRunSelection,
    isSqlMode,
    selectedConditionLikeRunsLoaded,
    selectedConditionLikeRunIdsForRequests,
    selectedIndexes,
    sqlScopeSignature,
    updateSqlState,
  ]);

  const setIndexLoadErrorForMode = useCallback((
    mode: SearchMode,
    nextError: string | null,
  ) => {
    setSearchTabState((prev) => {
      if (mode === 'vector') {
        if (prev.vectorSearch.error === nextError) return prev;
        return {
          ...prev,
          vectorSearch: { ...prev.vectorSearch, error: nextError },
        };
      }
      if (mode === 'agentic') {
        if (prev.agenticSearch.error === nextError) return prev;
        return {
          ...prev,
          agenticSearch: { ...prev.agenticSearch, error: nextError },
        };
      }
      if (mode === 'sql') {
        if (prev.sqlSearch.error === nextError) return prev;
        return {
          ...prev,
          sqlSearch: { ...prev.sqlSearch, error: nextError },
        };
      }
      return prev;
    });
  }, [setSearchTabState]);

  // Search bootstrap is one authoritative generation: URL state, index
  // catalog, canonical selection, searchable runs, then the browse dataset.
  useEffect(() => {
    if (!urlStateResolved) return;

    const generation = ++bootstrapGenerationRef.current;
    const ownsBootstrap = () => generation === bootstrapGenerationRef.current;
    const ownerMode = activeSearchModeRef.current;
    const params = new URLSearchParams(location.search);
    const explicitIndexIds = params.has('indexes') ? decodeCsv(params.get('indexes')) : [];
    const explicitRunIds = params.has('runs') ? decodeCsv(params.get('runs')) : null;
    const persistedIndexIds = selectedIndexes || [];
    const persistedRunIds = selectedRunIds;

    setUrlHydrated(false);
    setBootstrapStatus('loading');
    setIsLoadingIndexes(true);
    setHasLoadedIndexes(false);
    setIndexes([]);
    setIndexRuns({});
    setRunCatalogStatusByIndex({});
    setVectorDatasetStatus('idle');
    setConditionDatasetStatus('idle');
    setSqlDatasetStatus('idle');
    setSearchTabState((previous) => ({
      ...previous,
      selectedSearchResult: null,
      selectedResultFullVideoState: null,
      vectorSearch: {
        ...previous.vectorSearch,
        searchResults: [],
        hasSearched: pendingSubmittedRef.current,
        error: null,
      },
      conditionSearch: {
        ...previous.conditionSearch,
        searchResults: [],
        hasSearched: false,
        nextPageToken: null,
        nextCursor: null,
        error: null,
      },
      sqlSearch: {
        ...previous.sqlSearch,
        results: null,
        searchResults: [],
        hasSearched: false,
        scopeSignature: null,
        error: null,
      },
    }));

    const bootstrap = async () => {
      try {
        const catalog = await apiService.getIndexes({ includePreview: true });
        if (!ownsBootstrap()) return;

        const availableIndexIds = new Set(catalog.map((index) => index.index_id));
        const explicitIndexId = explicitIndexIds.find((indexId) => availableIndexIds.has(indexId)) || null;
        const persistedIndexId = persistedIndexIds.find((indexId) => availableIndexIds.has(indexId)) || null;
        const selectedIndexId = explicitIndexId || persistedIndexId || catalog[0]?.index_id || null;
        const selectedIndexIds = selectedIndexId ? [selectedIndexId] : null;

        if (explicitIndexIds.length > 1 && explicitIndexId) {
          setUrlNotice('Search supports one index at a time. The first available index was kept.');
        } else if (explicitIndexIds.length > 0 && !explicitIndexId && selectedIndexId) {
          setUrlNotice('The linked index is no longer available. Your available index was selected.');
        }

        let searchableRuns: PromptRun[] = [];
        if (selectedIndexId) {
          searchableRuns = (await apiService.getPromptRunsByIndex(selectedIndexId, 'searchable'))
            .filter((run) => isPromptRunSearchableStatus(run.status));
        }
        if (!ownsBootstrap()) return;

        const availableRunIds = new Set(searchableRuns.map((run) => run.run_id));
        const requestedRunIds = explicitRunIds ?? persistedRunIds;
        const selectedRunIds = requestedRunIds === null
          ? null
          : requestedRunIds.filter((runId) => availableRunIds.has(runId));
        const promptEntries: Array<readonly [string, Prompt] | null> = (
          activeSearchModeRef.current === 'agentic'
            ? []
            : await Promise.all(Array.from(new Set(
                searchableRuns
                  .map((run) => run.prompt_id)
                  .filter((promptId): promptId is string => Boolean(promptId)),
              )).map(async (promptId) => {
                try {
                  return [promptId, await apiService.getPrompt(promptId)] as const;
                } catch {
                  return null;
                }
              }))
        );
        if (!ownsBootstrap()) return;

        setIndexes(catalog);
        setHasLoadedIndexes(true);
        if (selectedIndexId) {
          setRunCatalogStatusByIndex({
            [selectedIndexId]: searchableRuns.length > 0 ? 'success' : 'empty',
          });
          setIndexRuns({ [selectedIndexId]: searchableRuns });
        } else {
          setRunCatalogStatusByIndex({});
          setIndexRuns({});
        }
        const resolvedPromptEntries = promptEntries.filter((entry): entry is readonly [string, Prompt] => entry !== null);
        setVectorRunPrompts(Object.fromEntries(resolvedPromptEntries));
        primeSearchRuntimeCatalog({
          indexes: catalog,
          runsByIndex: selectedIndexId ? { [selectedIndexId]: searchableRuns } : {},
          promptsById: Object.fromEntries(resolvedPromptEntries),
        });
        setSearchTabState((previous) => ({
          ...previous,
          selectedSearchResult: null,
          selectedResultFullVideoState: null,
          vectorSearch: {
            ...previous.vectorSearch,
            selectedIndexes: selectedIndexIds,
            selectedRunIds,
            searchResults: [],
            hasSearched: pendingSubmittedRef.current,
            error: null,
          },
          conditionSearch: {
            ...previous.conditionSearch,
            selectedIndexes: selectedIndexIds,
            selectedRunIds,
            searchResults: [],
            hasSearched: false,
            nextPageToken: null,
            nextCursor: null,
            error: null,
          },
          agenticSearch: {
            ...previous.agenticSearch,
            selectedIndexes: selectedIndexIds,
            selectedRunIds,
          },
          sqlSearch: {
            ...previous.sqlSearch,
            catalog: null,
            catalogScopeSignature: null,
            results: null,
            searchResults: [],
            hasSearched: false,
            scopeSignature: null,
            error: null,
          },
        }));
        setIndexLoadErrorForMode(ownerMode, null);
        setManualCanvasKind('browse');
        setBootstrapStatus(catalog.length > 0 ? 'success' : 'empty');
        setUrlHydrated(true);
        hasHydratedOnceRef.current = true;
      } catch (bootstrapError) {
        if (!ownsBootstrap()) return;
        setIndexes([]);
        setIndexRuns({});
        setRunCatalogStatusByIndex({});
        setHasLoadedIndexes(false);
        setBootstrapStatus('error');
        setIndexLoadErrorForMode(ownerMode, 'Failed to load the Search workspace');
        console.error('Failed to bootstrap Search workspace:', bootstrapError);
      } finally {
        if (ownsBootstrap()) {
          setIsLoadingIndexes(false);
        }
      }
    };

    void bootstrap();
    return () => {
      if (ownsBootstrap()) bootstrapGenerationRef.current += 1;
    };
  // URL resolution is the bootstrap boundary. Selection state is captured
  // from that resolved render and must not restart the same generation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, urlStateResolved]);

  // This is the single prompt-run loading trigger. Each mode/selection wave
  // owns a generation, so an older response cannot publish after a switch.
  useEffect(() => {
    if (!urlHydrated || !hasLoadedIndexes) return;

    // Persisted and URL-restored selections are provisional until the index
    // snapshot confirms ownership. Never dispatch work for a deleted/stale ID.
    const availableIndexIds = new Set(indexes.map((index) => index.index_id));
    const ownedSelectedIndexIds = selectedIndexIdsForRunLoading.filter(
      (indexId) => availableIndexIds.has(indexId),
    );

    const ownerKey = buildAsyncRequestOwnerKey(
      'search-prompt-runs',
      currentUser?.uid,
      searchMode,
      JSON.stringify(ownedSelectedIndexIds),
    );
    const token = {
      generation: promptRunLoadGenerationRef.current + 1,
      ownerKey,
    };
    promptRunLoadGenerationRef.current = token.generation;
    promptRunLoadOwnerKeyRef.current = ownerKey;

    ownedSelectedIndexIds.forEach((indexId) => {
      if (Object.prototype.hasOwnProperty.call(indexRuns, indexId)) return;
      void loadPromptRuns(indexId, {
        mode: searchMode,
        token,
      });
    });

    return () => {
      if (isCurrentAsyncRequest(
        promptRunLoadGenerationRef.current,
        promptRunLoadOwnerKeyRef.current,
        token,
      )) {
        promptRunLoadGenerationRef.current += 1;
        promptRunLoadOwnerKeyRef.current = '';
      }
    };
  }, [
    currentUser?.uid,
    hasLoadedIndexes,
    indexes,
    indexRuns,
    loadPromptRuns,
    searchMode,
    selectedIndexIdsForRunLoading,
    selectedIndexRunLoadSignature,
    urlHydrated,
  ]);

  const handleSortChange = (sortType: 'name' | 'date') => {
    if (indexSortBy === sortType) {
      // Same sort type clicked, toggle direction
      updateState({ indexSortAsc: !indexSortAsc });
    } else {
      // Different sort type, set new type with default direction
      updateState({
        indexSortBy: sortType,
        indexSortAsc: sortType === 'name' // Name defaults to A-Z, date defaults to newest first
      });
    }
  };

  // Load prompt when indexes change
  useEffect(() => {
    updateState({ selectedIndexPrompt: null });
  }, [indexes, selectedIndexes, updateState]);

  const executeSearch = async () => {
    if (searchMode === 'agentic' || !urlHydrated) return false;
    const hasIndexes = selectedIndexes && selectedIndexes.length > 0;
    const hasExplicitConditionRuns = Array.isArray(selectedConditionLikeRunIdsForRequests)
      && selectedConditionLikeRunIdsForRequests.length > 0;
    const usesAllSearchableRuns = selectedConditionLikeRunIdsForRequests === null;
    let sqlQueryForRequest = sqlQuery.trim();

    const isManualVectorSearch = searchMode === 'vector' && Boolean(query.trim() || searchImage);
    const isManualConditionSearch = searchMode === 'vector' && !query.trim() && !searchImage;
    let conditionRequestId: number | null = null;
    let sqlRequestId: number | null = null;
    let conditionController: AbortController | null = null;
    let sqlController: AbortController | null = null;
    let conditionRequestScopeSignature: string | null = null;
    let sqlRequestScopeSignature: string | null = null;
    vectorSearchRequestSeqRef.current += 1;
    cancelOutstandingVectorRequest();
    conditionSearchRequestSeqRef.current += 1;
    conditionLoadMoreRequestSeqRef.current += 1;
    cancelOutstandingConditionRequests();
    sqlSearchRequestSeqRef.current += 1;
    cancelOutstandingSqlExecutionRequests();
    if (searchMode === 'vector') setVectorIsLoading(false);
    const vectorRequestId = isManualVectorSearch ? vectorSearchRequestSeqRef.current : null;
    clearSelectedResultState();
    if (searchMode === 'sql') {
      updateSqlState({ results: null, searchResults: [], hasSearched: false, scopeSignature: null });
      setSqlDatasetStatus('idle');
    } else {
      updateManualState({ searchResults: [], hasSearched: false });
      setVectorDatasetStatus('idle');
      setConditionDatasetStatus('idle');
    }

    if (isManualConditionSearch && effectiveFilters.length === 0) {
      updateManualState({ hasSearched: false, searchResults: [], error: null });
      setVectorDatasetStatus('idle');
      setConditionDatasetStatus('idle');
      setManualCanvasKind('browse');
      return true;
    }

    if (isManualVectorSearch) {
      if (searchImageToken && isResolvingSearchImage) {
        updateState({ error: 'Attached image is still loading. Please retry in a moment.' });
        return false;
      }
      // Vector search requires query OR image
      if ((!query.trim() && !searchImage) || !hasIndexes) {
        updateState({ error: VECTOR_SEARCH_REQUIREMENTS_ERROR });
        return false;
      }
      if (isAwaitingLegacyConditionFilterMetadata) {
        updateManualState({ error: 'Loading filter field metadata. Please retry in a moment.' });
        return false;
      }
      const validation = validateManualSearchConditions({
        filters: effectiveFilters,
        fieldDefinitions,
      });
      if (!validation.ok) {
        updateManualState({ error: validation.message || 'Please fix your filter conditions.' });
        return false;
      }
    } else if (searchMode === 'sql') {
      if (!hasIndexes) {
        updateSqlState({ error: 'Please select at least one index' });
        return false;
      }
      if (!selectedConditionLikeRunsLoaded) {
        updateSqlState({ error: 'Loading searchable runs. Please retry in a moment.' });
        return false;
      }
      if (Array.isArray(selectedRunIds) && selectedRunIds.length === 0) {
        updateSqlState({ error: 'Please select at least one searchable extraction run' });
        return false;
      }
      if (!sqlQuery.trim()) {
        updateSqlState({ error: 'Please enter a SQL query' });
        return false;
      }
      const resolvedSqlQuery = resolveSqlQueryForScopedExecution({
        query: sqlQuery,
        catalog: activeSqlCatalog,
        catalogManagedQuery: sqlCatalogManagedQuery || sqlAutoQueryRef.current,
      });
      if (!resolvedSqlQuery.ok) {
        updateSqlState({ error: resolvedSqlQuery.error });
        return false;
      }
      sqlQueryForRequest = resolvedSqlQuery.query;
      if (resolvedSqlQuery.adoptedCatalogQuery) {
        sqlAutoQueryRef.current = resolvedSqlQuery.query;
        sqlQueryRef.current = resolvedSqlQuery.query;
        updateSqlState({
          query: resolvedSqlQuery.query,
          catalogManagedQuery: resolvedSqlQuery.catalogManagedQuery,
          error: null,
        });
      }
    } else {
      // Filter search requires filters
      if (!hasIndexes) {
        updateState({ error: 'Please select at least one index' });
        return false;
      }
      if (!selectedConditionLikeRunsLoaded) {
        updateState({ error: 'Loading searchable runs. Please retry in a moment.' });
        return false;
      }
      if (!hasExplicitConditionRuns && !usesAllSearchableRuns) {
        updateState({ error: 'Please select at least one completed extraction run' });
        return false;
      }
      if (isAwaitingLegacyConditionFilterMetadata) {
        updateState({ error: 'Loading filter field metadata. Please retry in a moment.' });
        return false;
      }
      const validation = validateConditions(effectiveFilters, fieldDefinitions);
      if (!validation.ok) {
        updateState({ error: validation.message || 'Please fix your filter conditions.' });
        return false;
      }
    }

    const vectorController = isManualVectorSearch ? new AbortController() : null;
    if (vectorController) vectorSearchAbortRef.current = vectorController;

    setIsLoading(true);
    if (searchMode === 'sql') {
      cancelOutstandingSqlRequests();
      sqlRequestId = ++sqlSearchRequestSeqRef.current;
      setSqlDatasetStatus('loading');
      updateSqlState({
        error: null,
        hasSearched: true,
        results: null,
        searchResults: [],
        scopeSignature: null,
      });
    } else {
      updateManualState({ error: null, hasSearched: true, searchResults: [] });
      if (isManualConditionSearch) {
        cancelOutstandingConditionRequests();
        conditionRequestId = ++conditionSearchRequestSeqRef.current;
        setConditionDatasetStatus('loading');
        setIsLoadingMore(false);
        setSearchTabState((previous) => ({
          ...previous,
          conditionSearch: {
            ...previous.conditionSearch,
            nextPageToken: null,
            nextCursor: null,
          },
        }));
      } else {
        setVectorDatasetStatus('loading');
      }
    }

    try {
      let results: SearchResult[];

      if (isManualVectorSearch) {
        const hasText = query.trim().length > 0;
        const hasImage = !!searchImage;

        if (hasText && hasImage) {
          // Multimodal search - both text and image provided
          const multimodalResults = await apiService.searchMultimodal(firstSelectedIndex, {
            textQuery: query.trim(),
            imageData: searchImage,
            topK: 100,
            textWeight: 0.5,
            imageWeight: 0.5,
            runIds: selectedRunIds,
            searchFields: effectiveSearchFields,
            indexIds: selectedIndexes,
            view: 'gallery',
          }, vectorController!.signal);
          results = multimodalResults as SearchResult[];
        } else if (hasImage) {
          // Image-only search
          results = await apiService.searchByImage(
            firstSelectedIndex,
            searchImage,
            100,
            selectedRunIds,
            selectedIndexes,
            vectorController!.signal,
            'gallery',
          );
        } else {
          // Text-only search
          results = await apiService.searchVideos(firstSelectedIndex, {
            query: query.trim(),
            top_k: 100,
            search_fields: effectiveSearchFields,
            run_ids: selectedRunIds,
            index_ids: selectedIndexes,
            view: 'gallery',
          }, vectorController!.signal);
        }
        if (
          vectorController?.signal.aborted
          || vectorRequestId !== vectorSearchRequestSeqRef.current
          || activeSearchModeRef.current !== 'vector'
        ) {
          return false;
        }
        const locallyFilteredResults = applyFilters(results, effectiveFilters);
        updateManualState({
          searchResults: results,
          hasSearched: true,
          error: null,
        });
        setVectorDatasetStatus(locallyFilteredResults.length > 0 ? 'success' : 'empty');
        resetResultsCanvasScroll();
        setManualCanvasKind('search');
        if (locallyFilteredResults.length === 0 && effectiveFilters.length > 0) {
          updateManualState({ error: null });
        }
      } else if (searchMode === 'sql') {
        sqlController = new AbortController();
        sqlSearchAbortRef.current = sqlController;
        sqlRequestScopeSignature = sqlScopeSignature;
        const requestId = sqlRequestId;

        const response = await apiService.executeSqlSearch(
          firstSelectedIndex,
          sqlQueryForRequest,
          {
            runIds: selectedConditionLikeRunIdsForRequests,
            indexIds: selectedIndexes,
          },
          sqlController.signal,
        );

        if (
          sqlController.signal.aborted
          || requestId !== sqlSearchRequestSeqRef.current
          || activeSearchModeRef.current !== 'sql'
          || sqlRequestScopeSignature !== currentSqlScopeSignatureRef.current
        ) {
          return false;
        }

        updateSqlState({
          error: null,
          hasSearched: true,
          results: response,
          searchResults: buildSqlSearchResultsFromExecuteResponse(response),
          scopeSignature: sqlRequestScopeSignature,
        });
        setSqlDatasetStatus(response.rows.length > 0 ? 'success' : 'empty');
        resetResultsCanvasScroll();
        setManualCanvasKind('sql');
      } else {
        const requestId = conditionRequestId;
        conditionController = new AbortController();
        conditionSearchAbortRef.current = conditionController;
        conditionRequestScopeSignature = conditionSearchScopeSignature;

        const response = await apiService.filterSearch(
          firstSelectedIndex,
          effectiveFilters,
          selectedConditionLikeRunIdsForRequests,
          selectedIndexes,
          50,
          undefined,
          conditionController.signal,
        );

        if (
          conditionController.signal.aborted
          || requestId !== conditionSearchRequestSeqRef.current
          || activeSearchModeRef.current !== 'vector'
          || conditionRequestScopeSignature !== currentConditionScopeSignatureRef.current
        ) {
          return false;
        }

        const nextResultSignature = buildSearchResultIdentitySignature(response.results);
        conditionCursorContextRef.current = {
          cursor: response.next_page_token || null,
          scopeSignature: conditionRequestScopeSignature,
          resultSignature: nextResultSignature,
        };

        setSearchTabState((prev) => ({
          ...prev,
          vectorSearch: {
            ...prev.vectorSearch,
            error: null,
            hasSearched: true,
            searchResults: response.results,
          },
          conditionSearch: {
            ...prev.conditionSearch,
            nextPageToken: response.next_page_token || null,
          }
        }));
        setConditionDatasetStatus(response.results.length > 0 ? 'success' : 'empty');
        resetResultsCanvasScroll();
        setManualCanvasKind('search');
      }
      return true;
    } catch (err) {
      if (
        isManualVectorSearch
        && (
          vectorController?.signal.aborted
          || vectorRequestId !== vectorSearchRequestSeqRef.current
          || isAbortedRequestError(err)
        )
      ) {
        return false;
      }
      if (
        isManualConditionSearch
        && (
          conditionController?.signal.aborted
          || conditionRequestId !== conditionSearchRequestSeqRef.current
          || activeSearchModeRef.current !== 'vector'
          || conditionRequestScopeSignature !== currentConditionScopeSignatureRef.current
          || isAbortedRequestError(err)
        )
      ) {
        return false;
      }
      if (
        searchMode === 'sql'
        && (
          sqlController?.signal.aborted
          || sqlRequestId !== sqlSearchRequestSeqRef.current
          || activeSearchModeRef.current !== 'sql'
          || sqlRequestScopeSignature !== currentSqlScopeSignatureRef.current
          || isAbortedRequestError(err)
        )
      ) {
        return false;
      }
      if (searchMode === 'sql') {
        updateSqlState({
          error: formatApiError(err),
          hasSearched: true,
          results: null,
          searchResults: [],
          scopeSignature: null,
        });
        setSqlDatasetStatus('error');
      } else {
        updateManualState({ error: formatApiError(err) });
        if (isManualConditionSearch) {
          setConditionDatasetStatus('error');
        } else {
          setVectorDatasetStatus('error');
        }
      }
      console.error('Search error:', err);
      return false;
    } finally {
      if (conditionSearchAbortRef.current === conditionController) {
        conditionSearchAbortRef.current = null;
      }
      if (sqlSearchAbortRef.current === sqlController) {
        sqlSearchAbortRef.current = null;
      }
      if (vectorSearchAbortRef.current === vectorController) {
        vectorSearchAbortRef.current = null;
      }
      const ownsLoadingState = isManualVectorSearch
        ? vectorRequestId === vectorSearchRequestSeqRef.current
        : isManualConditionSearch
          ? conditionRequestId === conditionSearchRequestSeqRef.current
          : sqlRequestId === sqlSearchRequestSeqRef.current;
      if (ownsLoadingState) {
        setIsLoading(false);
      }
    }
  };
  executeSearchRef.current = executeSearch;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowSearchFieldsDropdown(false);
    updateState({ showIndexDropdown: false, showRunDropdown: false });
    historyModeRef.current = 'push';
    await executeSearch();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      updateState({ error: 'Please upload a valid image file.' });
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
      return;
    }

    if (file.size > MAX_MEDIA_SEARCH_IMAGE_BYTES) {
      updateState({ error: 'Image is too large. Please upload an image under 8 MB.' });
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
      return;
    }

    const uploadRequestId = ++imageUploadRequestRef.current;
    const uploadUserId = currentUser?.uid;
    const reader = new FileReader();
    reader.onerror = () => {
      if (uploadRequestId !== imageUploadRequestRef.current) return;
      updateState({ error: 'Failed to read the selected image. Please try another file.' });
    };
    reader.onloadend = () => {
      if (uploadRequestId !== imageUploadRequestRef.current) return;
      const result = typeof reader.result === 'string' ? reader.result : '';
      const base64 = result.includes(',') ? result.split(',')[1] : '';
      if (!base64) {
        updateState({ error: 'Failed to parse image data. Please try another file.' });
        return;
      }

      const tokenToReplace = searchImageTokenRef.current;
      setSearchImage(base64);
      setHydratedSearchImageToken(null);
      setUrlNotice(null);

      void (async () => {
        const stored = await storeImagePayload(base64, uploadUserId);
        if (uploadRequestId !== imageUploadRequestRef.current) {
          void removeImagePayload(stored.token, uploadUserId);
          return;
        }
        setSearchTabState((prev) => ({
          ...prev,
          vectorSearch: {
            ...prev.vectorSearch,
            searchImageToken: stored.token,
            error: null,
          },
        }));
        setHydratedSearchImageToken(stored.token);
        searchImageTokenRef.current = stored.token;
        if (tokenToReplace && tokenToReplace !== stored.token) {
          void removeImagePayload(tokenToReplace, uploadUserId);
        }
        if (stored.persistence === 'volatile') {
          setUrlNotice('Image attachment is available in this tab only and may not persist after reload.');
        } else {
          setUrlNotice(null);
        }
        if (imageInputRef.current) {
          imageInputRef.current.value = '';
        }
      })().catch(() => {
        if (uploadRequestId !== imageUploadRequestRef.current) return;
        updateState({ error: 'Failed to persist uploaded image in browser storage.' });
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    imageUploadRequestRef.current += 1;
    searchImageTokenRef.current = null;
    if (searchImageToken) {
      void removeImagePayload(searchImageToken, currentUser?.uid);
    }
    setSearchImage(null);
    setHydratedSearchImageToken(null);
    setUrlNotice(null);
    setSearchTabState((prev) => ({
      ...prev,
      vectorSearch: {
        ...prev.vectorSearch,
        searchImageToken: null,
      },
    }));
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleClearAllSearch = useCallback(() => {
    setManualCanvasKind('browse');
    historyModeRef.current = 'replace';
    pendingSubmittedRef.current = false;
    pendingDetailRef.current = null;
    pendingSessionRef.current = null;

    vectorSearchRequestSeqRef.current += 1;
    conditionSearchRequestSeqRef.current += 1;
    conditionLoadMoreRequestSeqRef.current += 1;
    sqlSearchRequestSeqRef.current += 1;
    cancelOutstandingVectorRequest();
    cancelOutstandingConditionRequests();
    cancelOutstandingSqlRequests();

    setVectorIsLoading(false);
    setSqlIsLoading(false);
    setIsLoadingMore(false);
    setSqlCatalogIsLoading(false);
    setVectorDatasetStatus('idle');
    setConditionDatasetStatus('idle');
    setSqlDatasetStatus('idle');
    setShowSearchFieldsDropdown(false);
    setUrlNotice(null);

    imageUploadRequestRef.current += 1;
    imageHydrationRequestRef.current += 1;
    const imageTokenToRemove = searchImageTokenRef.current;
    searchImageTokenRef.current = null;
    if (imageTokenToRemove) {
      void removeImagePayload(imageTokenToRemove, currentUser?.uid);
    }
    setSearchImage(null);
    setHydratedSearchImageToken(null);
    setIsResolvingSearchImage(false);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }

    sqlAutoQueryRef.current = '';
    previousSqlScopeSignatureRef.current = null;
    currentConditionScopeSignatureRef.current = '';
    currentSqlScopeSignatureRef.current = '';
    currentConditionResultSignatureRef.current = '';
    conditionCursorContextRef.current = {
      cursor: null,
      scopeSignature: '',
      resultSignature: '',
    };

    setSearchTabState((prev) => ({
      ...prev,
      selectedSearchResult: null,
      selectedResultFullVideoState: null,
      vectorSearch: {
        ...prev.vectorSearch,
        query: '',
        searchImageToken: null,
        searchResults: [],
        error: null,
        filters: [],
        searchFields: null,
        showIndexDropdown: false,
        showRunDropdown: false,
        showFilterPanel: false,
        selectedIndexPrompt: null,
        showPromptDetails: false,
        hasSearched: false,
      },
      conditionSearch: {
        ...prev.conditionSearch,
        searchResults: [],
        error: null,
        filters: [],
        showIndexDropdown: false,
        showRunDropdown: false,
        showFilterPanel: false,
        selectedIndexPrompt: null,
        showPromptDetails: false,
        hasSearched: false,
        nextPageToken: null,
        nextCursor: null,
      },
      sqlSearch: {
        ...prev.sqlSearch,
        query: '',
        assistantPrompt: '',
        catalog: null,
        catalogScopeSignature: null,
        catalogManagedQuery: null,
        results: null,
        searchResults: [],
        error: null,
        hasSearched: false,
        scopeSignature: null,
      },
    }));
  }, [
    cancelOutstandingConditionRequests,
    cancelOutstandingSqlRequests,
    cancelOutstandingVectorRequest,
    currentUser?.uid,
    setManualCanvasKind,
    setSearchTabState,
  ]);

  const handleLoadMore = async () => {
    if (
      !nextPageToken
      || isLoadingMore
      || searchMode !== 'vector'
      || Boolean(query.trim() || searchImage)
    ) return;
    if (!selectedConditionLikeRunsLoaded) {
      updateState({ error: 'Loading searchable runs. Please retry in a moment.' });
      return;
    }

    const currentResultSignature = currentConditionResultSignatureRef.current;
    const cursorContext = conditionCursorContextRef.current;
    if (
      cursorContext.cursor !== nextPageToken
      || cursorContext.scopeSignature !== currentConditionScopeSignatureRef.current
      || cursorContext.resultSignature !== currentResultSignature
    ) {
      updateState({ error: 'Condition search pagination became stale. Please rerun the search.' });
      return;
    }

    conditionLoadMoreAbortRef.current?.abort();
    const requestId = ++conditionLoadMoreRequestSeqRef.current;
    const controller = new AbortController();
    conditionLoadMoreAbortRef.current = controller;
    setIsLoadingMore(true);
    try {
      const response = await apiService.filterSearch(
        firstSelectedIndex,
        effectiveFilters,
        selectedConditionLikeRunIdsForRequests,
        selectedIndexes,
        50,
        nextPageToken,
        controller.signal,
      );

      if (
        controller.signal.aborted
        || requestId !== conditionLoadMoreRequestSeqRef.current
        || activeSearchModeRef.current !== 'vector'
        || currentConditionScopeSignatureRef.current !== cursorContext.scopeSignature
        || currentConditionResultSignatureRef.current !== currentResultSignature
      ) {
        return;
      }

      const mergedResults = [...searchResults, ...response.results];
      const mergedResultSignature = buildSearchResultIdentitySignature(mergedResults);
      currentConditionResultSignatureRef.current = mergedResultSignature;
      conditionCursorContextRef.current = {
        cursor: response.next_page_token || null,
        scopeSignature: currentConditionScopeSignatureRef.current,
        resultSignature: mergedResultSignature,
      };

      setSearchTabState((prev) => ({
        ...prev,
        vectorSearch: {
          ...prev.vectorSearch,
          error: null,
          searchResults: mergedResults,
        },
        conditionSearch: {
          ...prev.conditionSearch,
          nextPageToken: response.next_page_token || null,
        },
      }));
    } catch (err) {
      if (
        isAbortedRequestError(err)
        || conditionLoadMoreAbortRef.current !== controller
        || requestId !== conditionLoadMoreRequestSeqRef.current
      ) {
        return;
      }
      updateState({ error: formatApiError(err) });
      console.error('Load more error:', err);
    } finally {
      if (
        conditionLoadMoreAbortRef.current === controller
        && requestId === conditionLoadMoreRequestSeqRef.current
      ) {
        conditionLoadMoreAbortRef.current = null;
        setIsLoadingMore(false);
      }
    }
  };

  // Helper to get all runs from all selected indexes
  const getAllSelectedIndexRuns = () => {
    if (!selectedIndexes || selectedIndexes.length === 0) return [];
    return selectedIndexes.flatMap(indexId => indexRuns[indexId] || []);
  };

  // Type for field with source information
  interface FieldWithSource {
    name: string;
    label?: string;
    type: 'unknown' | 'string' | 'integer' | 'number' | 'boolean' | 'array';
    description?: string;
    itemType?: 'string' | 'integer' | 'number' | 'boolean' | 'object';
    searchable?: boolean;
    sourceIndexId?: string;
    sourceRunId?: string;
    sourceRunName?: string;
  }

  // Grouped fields structure for hierarchical display
  interface GroupedFields {
    indexId: string;
    indexName: string;
    runs: {
      runId: string;
      runName: string;
      fields: FieldWithSource[];
    }[];
  }

  // Extract field definitions with source tracking for grouping
  const { fieldDefinitions, groupedFieldDefinitions } = useMemo(() => {
    const allFields: FieldWithSource[] = [];
    const groupedByIndex = new Map<string, GroupedFields>();

    // Helper to add fields with source tracking
    const addFieldsFromRun = (indexId: string, indexName: string, runId: string, runName: string, fields: FieldDefinition[]) => {
      // Add to grouped structure
      if (!groupedByIndex.has(indexId)) {
        groupedByIndex.set(indexId, { indexId, indexName, runs: [] });
      }
      const indexGroup = groupedByIndex.get(indexId)!;

      const runFields: FieldWithSource[] = fields.map(f => ({
        ...f,
        sourceIndexId: indexId,
        sourceRunId: runId,
        sourceRunName: runName
      }));

      // Check if run already exists in group
      const existingRun = indexGroup.runs.find(r => r.runId === runId);
      if (existingRun) {
        // Merge fields
        runFields.forEach(field => {
          if (!existingRun.fields.some(f => f.name === field.name)) {
            existingRun.fields.push(field);
          }
        });
      } else {
        indexGroup.runs.push({ runId, runName, fields: runFields });
      }

      allFields.push(...runFields);
    };

    if (!selectedIndexes || selectedIndexes.length === 0) {
      return { fieldDefinitions: [], groupedFieldDefinitions: [] };
    }

    // Process each selected index
    selectedIndexes.forEach(indexId => {
      const indexInfo = indexes.find(idx => idx.index_id === indexId);
      const indexName = indexInfo?.name || 'Index';
      const runsForIndex = indexRuns[indexId] || [];

      // If specific runs are selected, only include fields from those runs
      const targetRuns = selectedRunIds && selectedRunIds.length > 0
        ? runsForIndex.filter(r => selectedRunIds.includes(r.run_id))
        : runsForIndex;

      targetRuns.forEach(run => {
        if (run.prompt_id && runPrompts[run.prompt_id]) {
          const fields = extractFieldsFromResults(
            searchResults.filter((result: SearchResult) => getResultRunId(result) === run.run_id),
            runPrompts[run.prompt_id].json_schema,
          );
          addFieldsFromRun(indexId, indexName, run.run_id, run.prompt_name, fields);
        }
      });
    });

    // If no fields found from prompts, extract from search results
    if (allFields.length === 0 && searchResults.length > 0) {
      const extractedFields = extractFieldsFromResults(searchResults, null);
      const indexId = selectedIndexes[0];
      const indexInfo = indexes.find(idx => idx.index_id === indexId);
      addFieldsFromRun(indexId, indexInfo?.name || 'Index', 'results', 'Search Results', extractedFields);
    }

    return {
      fieldDefinitions: mergeFieldDefinitions(allFields),
      groupedFieldDefinitions: Array.from(groupedByIndex.values())
    };
  }, [searchResults, selectedRunIds, indexRuns, selectedIndexes, runPrompts, indexes]);

  const searchableFieldDefinitions = useMemo(
    () => fieldDefinitions.filter((field) => field.searchable !== false),
    [fieldDefinitions],
  );

  useEffect(() => {
    if (showSearchFieldsDropdown && (isLoading || searchableFieldDefinitions.length === 0)) {
      setShowSearchFieldsDropdown(false);
    }
  }, [isLoading, searchableFieldDefinitions.length, showSearchFieldsDropdown]);

  const selectedRunPromptLoadState = useMemo(() => {
    if (!selectedIndexes || selectedIndexes.length === 0) {
      return {
        runsLoadedForAllSelectedIndexes: false,
        promptsLoadedForTargetRuns: false,
        targetRuns: [] as any[],
      };
    }

    const runsLoadedForAllSelectedIndexes = selectedIndexes.every(
      (indexId) => Array.isArray(indexRuns[indexId]),
    );
    if (!runsLoadedForAllSelectedIndexes) {
      return {
        runsLoadedForAllSelectedIndexes,
        promptsLoadedForTargetRuns: false,
        targetRuns: [] as any[],
      };
    }

    const runsForSelectedIndexes = selectedIndexes.flatMap((indexId) => indexRuns[indexId] || []);
    const targetRuns = selectedRunIds && selectedRunIds.length > 0
      ? runsForSelectedIndexes.filter((run) => selectedRunIds.includes(run.run_id))
      : runsForSelectedIndexes;
    const promptsLoadedForTargetRuns = targetRuns.every(
      (run) => !run.prompt_id || Boolean(runPrompts[run.prompt_id]),
    );

    return {
      runsLoadedForAllSelectedIndexes,
      promptsLoadedForTargetRuns,
      targetRuns,
    };
  }, [indexRuns, runPrompts, selectedIndexes, selectedRunIds]);

  const hasCompleteSelectedRunPromptMetadata = useMemo(
    () => (
      selectedIndexes !== null
      && selectedIndexes.length > 0
      && selectedRunPromptLoadState.runsLoadedForAllSelectedIndexes
      && selectedRunPromptLoadState.promptsLoadedForTargetRuns
    ),
    [selectedIndexes, selectedRunPromptLoadState],
  );

  useEffect(() => {
    if (
      !isConditionLikeMode
      || !selectedIndexes
      || selectedIndexes.length === 0
      || !selectedRunPromptLoadState.runsLoadedForAllSelectedIndexes
    ) {
      return;
    }

    const allSelectedRuns = selectedIndexes.flatMap((indexId) => indexRuns[indexId] || []);
    const availableRunIds = allSelectedRuns.map((run) => run.run_id);
    const availableRunIdSet = new Set(availableRunIds);

    if (selectedRunIds === null) {
      // Null is the canonical optional scope: all searchable runs. Keeping it
      // compact also means newly completed runs join the browse baseline.
      return;
    }

    const nextRunIds = selectedRunIds.filter((runId) => availableRunIdSet.has(runId));

    if (nextRunIds.length === selectedRunIds.length) {
      return;
    }

    applySharedWorkspaceScope({
      indexIds: selectedIndexes,
      runIds: nextRunIds.length > 0 ? nextRunIds : null,
    });
  }, [
    applySharedWorkspaceScope,
    indexRuns,
    isConditionLikeMode,
    selectedIndexes,
    selectedRunIds,
    selectedRunPromptLoadState.runsLoadedForAllSelectedIndexes,
  ]);

  const dynamicNestedFilterPrefixes = useMemo(() => {
    const prefixes = new Set<string>();
    selectedRunPromptLoadState.targetRuns.forEach((run) => {
      if (run.prompt_id && runPrompts[run.prompt_id]) {
        collectDynamicNestedFilterPrefixesFromSchema(
          runPrompts[run.prompt_id].json_schema,
          [],
          prefixes,
        );
      }
    });
    return Array.from(prefixes);
  }, [runPrompts, selectedRunPromptLoadState]);

  const effectiveFilters = useMemo(
    () => normalizeConditionFilters(filters, {
      availableFields: fieldDefinitions,
      dynamicNestedPrefixes: dynamicNestedFilterPrefixes,
    }),
    [dynamicNestedFilterPrefixes, fieldDefinitions, filters],
  );

  const prioritizedFilterFieldPaths = useMemo(() => Array.from(new Set(
    effectiveFilters
      .map((filter) => String(filter.field || '').trim())
      .filter(Boolean),
  )), [effectiveFilters]);

  const conditionSearchScopeSignature = useMemo(() => JSON.stringify({
    indexes: [...(selectedIndexes || [])].sort(),
    runs: [...(selectedConditionLikeRunIdsForRequests || [])].sort(),
    filters: effectiveFilters.map((filter) => ({
      field: filter.field,
      operator: canonicalizeOperator(filter.operator),
      type: filter.type,
      value: filter.value,
    })),
  }), [
    effectiveFilters,
    selectedIndexes,
    selectedConditionLikeRunIdsForRequests,
  ]);

  useEffect(() => {
    currentConditionScopeSignatureRef.current = conditionSearchScopeSignature;
  }, [conditionSearchScopeSignature]);

  useEffect(() => {
    currentSqlScopeSignatureRef.current = sqlScopeSignature;
  }, [sqlScopeSignature]);

  useEffect(() => {
    currentConditionResultSignatureRef.current = buildSearchResultIdentitySignature(
      searchTabState.vectorSearch.searchResults || [],
    );
  }, [searchTabState.vectorSearch.searchResults]);

  const isAwaitingLegacyConditionFilterMetadata = useMemo(() => {
    if (searchMode !== 'vector') {
      return false;
    }

    const hasUnresolvedLegacyFilter = filters.some((filter) => {
      const fieldName = (filter.field || '').trim();
      if (!fieldName.includes('.') || fieldName.startsWith('__nested__:')) {
        return false;
      }

      if (fieldDefinitions.some((field) => field.name === fieldName)) {
        return false;
      }

      const canonicalFieldName = canonicalizeFilterFieldName(
        fieldName,
        fieldDefinitions,
        { dynamicNestedPrefixes: dynamicNestedFilterPrefixes },
      );
      return fieldName.includes('.')
        && canonicalFieldName === fieldName;
    });

    if (!hasUnresolvedLegacyFilter) {
      return false;
    }

    return !hasCompleteSelectedRunPromptMetadata;
  }, [
    dynamicNestedFilterPrefixes,
    fieldDefinitions,
    filters,
    hasCompleteSelectedRunPromptMetadata,
    searchMode,
  ]);

  const conditionValidation = useMemo(() => {
    if (filters.length === 0) {
      return { ok: true as const };
    }
    return validateConditions(effectiveFilters, fieldDefinitions);
  }, [effectiveFilters, fieldDefinitions, filters.length]);

  const effectiveSearchFields = useMemo(
    () => sanitizeSearchFieldSelection(
      searchFields,
      searchableFieldDefinitions.map((field) => field.name),
      {
        metadataComplete: hasCompleteSelectedRunPromptMetadata,
        loadedFieldNames: fieldDefinitions.map((field) => field.name),
        loadedFieldDefinitions: fieldDefinitions,
      },
    ),
    [fieldDefinitions, hasCompleteSelectedRunPromptMetadata, searchFields, searchableFieldDefinitions],
  );

  const searchableGroupedFieldDefinitions = useMemo(
    () => groupedFieldDefinitions
      .map((indexGroup) => ({
        ...indexGroup,
        runs: indexGroup.runs
          .map((runGroup) => ({
            ...runGroup,
            fields: runGroup.fields.filter((field) => field.searchable !== false),
          }))
          .filter((runGroup) => runGroup.fields.length > 0),
      }))
      .filter((indexGroup) => indexGroup.runs.length > 0),
    [groupedFieldDefinitions],
  );

  useEffect(() => {
    if (searchMode !== 'vector') {
      return;
    }

    if (areSearchFieldSelectionsEqual(searchFields, effectiveSearchFields)) {
      return;
    }

    setSearchTabState((prev) => ({
      ...prev,
      vectorSearch: {
        ...prev.vectorSearch,
        searchFields: effectiveSearchFields,
      },
    }));
  }, [effectiveSearchFields, searchFields, searchMode, setSearchTabState]);

  useEffect(() => {
    if (areConditionFiltersEqual(filters, effectiveFilters)) {
      return;
    }

    setSearchTabState((prev) => ({
        ...prev,
        vectorSearch: {
          ...prev.vectorSearch,
          filters: effectiveFilters,
        },
      }));
  }, [effectiveFilters, filters, setSearchTabState]);

  useEffect(() => {
    if (searchMode !== 'sql') {
      previousSqlScopeSignatureRef.current = null;
      return;
    }
    if (!selectedConditionLikeRunsLoaded) {
      return;
    }

    const previousSignature = previousSqlScopeSignatureRef.current;
    previousSqlScopeSignatureRef.current = sqlScopeSignature;
    if (
      previousSignature === null
      || previousSignature === sqlScopeSignature
      || sqlState.scopeSignature === sqlScopeSignature
    ) {
      return;
    }

    cancelOutstandingSqlExecutionRequests();
    sqlSearchRequestSeqRef.current += 1;
    setSqlIsLoading(false);
    setSqlDatasetStatus('idle');
    clearSelectedResultState();
    updateSqlState({
      catalog: null,
      catalogScopeSignature: null,
      error: null,
      hasSearched: false,
      results: null,
      searchResults: [],
      scopeSignature: null,
    });
  }, [
    cancelOutstandingSqlExecutionRequests,
    clearSelectedResultState,
    searchMode,
    selectedConditionLikeRunsLoaded,
    setSqlIsLoading,
    sqlState.scopeSignature,
    sqlScopeSignature,
    updateSqlState,
  ]);

  useEffect(() => {
    if (!isActive || !urlHydrated || searchMode === 'agentic' || isLoading || isResolvingSearchImage) return;
    // Ensure URL replay waits for async image-token hydration to settle for the active token.
    if (searchImageToken && hydratedSearchImageToken !== searchImageToken) return;
    if (!pendingSubmittedRef.current) return;
    if (isAwaitingLegacyConditionFilterMetadata) return;
    if (searchMode === 'sql') {
      if (!selectedConditionLikeRunsLoaded || hasExplicitEmptySqlRunSelection) return;
      if (sqlCatalogIsLoading || !activeSqlCatalog) return;
    }

    pendingSubmittedRef.current = false;
    executeSearchRef.current().catch((err) => {
      console.error('URL-triggered search failed:', err);
    });
  }, [
    isActive,
    hydratedSearchImageToken,
    isAwaitingLegacyConditionFilterMetadata,
    isLoading,
    isResolvingSearchImage,
    searchImageToken,
    searchMode,
    selectedConditionLikeRunsLoaded,
    hasExplicitEmptySqlRunSelection,
    sqlCatalogIsLoading,
    activeSqlCatalog,
    submittedReplayToken,
    urlHydrated,
  ]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current || pendingDetailRef.current !== null || pendingSessionRef.current !== null) return;

    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';
    const shouldPersistFilters = !isAgenticMode && !isSqlMode && effectiveFilters.some((filter: FilterCondition) => hasMeaningfulFilter(filter));
    const encodedFilters = shouldPersistFilters ? encodeJsonToBase64Url(effectiveFilters) : null;
    const encodedIndexes = encodeCsv(selectedIndexes || []);
    const encodedRuns = encodeCsv(selectedRunIdsForUrl || []);
    const encodedFields = searchMode === 'vector' && effectiveSearchFields ? encodeCsv(effectiveSearchFields) : null;

    lastCommittedSearchRef.current = commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        mode: searchMode === 'sql' ? 'sql' : null,
        indexes: encodedIndexes || null,
        runs: encodedRuns || null,
        q: searchMode === 'vector'
          ? query || null
          : searchMode === 'sql'
            ? sqlQuery || null
            : null,
        fields: encodedFields || null,
        filters: encodedFilters,
        submitted: !isAgenticMode && hasSearched ? 1 : null,
        imgToken: searchImageToken || null,
        detail: selectedDetailToken,
        session: isAgenticMode ? agenticStoreState.activeSessionId || null : null,
        panel: isAgenticMode ? 'agent' : 'search',
        view: searchView,
      },
      mode,
    });
  }, [
    isActive,
    agenticStoreState.activeSessionId,
    effectiveSearchFields,
    effectiveFilters,
    hasSearched,
    isAgenticMode,
    isSqlMode,
    location,
    navigate,
    query,
    sqlQuery,
    searchImageToken,
    searchMode,
    searchView,
    selectedIndexes,
    selectedRunIdsForUrl,
    selectedDetailToken,
    urlHydrated,
  ]);

  // Apply filters to search results
  const filteredResults = useMemo<SearchResult[]>(() => {
    // In filter mode, results are already filtered by backend
    if (searchMode === 'sql') {
      return searchResults;
    }
    // In vector mode, apply filters on frontend
    return applyFilters(searchResults, effectiveFilters).map((result) => withSearchFilterMatches(result, effectiveFilters));
  }, [effectiveFilters, searchResults, searchMode]);

  const selectedGalleryRuns = useMemo<PromptRun[]>(() => {
    if (!selectedIndexes?.length) return [];
    const available = selectedIndexes.flatMap((indexId) => indexRuns[indexId] || []);
    if (!selectedRunIds) return available;
    const selected = new Set(selectedRunIds);
    return available.filter((run) => selected.has(run.run_id));
  }, [indexRuns, selectedIndexes, selectedRunIds]);
  const selectedRunCatalogStatus: DatasetStatus = firstSelectedIndex
    ? runCatalogStatusByIndex[firstSelectedIndex] || 'loading'
    : hasLoadedIndexes
      ? 'empty'
      : 'loading';
  const retrySelectedRunCatalog = useCallback(() => {
    if (!firstSelectedIndex) return;
    setRunCatalogStatusByIndex((previous) => ({ ...previous, [firstSelectedIndex]: 'loading' }));
    setIndexRuns((previous) => {
      const next = { ...previous };
      delete next[firstSelectedIndex];
      return next;
    });
  }, [firstSelectedIndex]);
  const browseGallery = useBrowseGallery({
    indexId: firstSelectedIndex || null,
    runs: selectedGalleryRuns,
    enabled: urlHydrated
      && selectedConditionLikeRunsLoaded
      && selectedRunCatalogStatus !== 'error'
      && !isAgenticMode
      && manualCanvasKind === 'browse',
  });
  const {
    hasMoreVideos: browseHasMoreVideos,
    isLoadingMoreVideos: isLoadingMoreBrowseVideos,
    loadMoreVideos: loadMoreBrowseVideos,
  } = browseGallery;
  useEffect(() => {
    const sentinel = browsePaginationSentinelRef.current;
    const root = resultsCanvasRef.current;
    if (
      !isActive
      || !sentinel
      || !root
      || !browseHasMoreVideos
      || isLoadingMoreBrowseVideos
      || isAgenticMode
      || manualCanvasKind !== 'browse'
      || typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        void loadMoreBrowseVideos();
      }
    }, {
      root,
      rootMargin: '600px 0px',
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    isActive,
    browseHasMoreVideos,
    isLoadingMoreBrowseVideos,
    loadMoreBrowseVideos,
    isAgenticMode,
    manualCanvasKind,
  ]);
  const resultsCanvasKey = useMemo(() => [
    isAgenticMode ? 'agent' : manualCanvasKind,
    isAgenticMode ? (agenticStoreState.activeSessionId || 'draft') : browseGallery.scopeKey,
    searchView,
  ].join('::'), [
    agenticStoreState.activeSessionId,
    browseGallery.scopeKey,
    isAgenticMode,
    manualCanvasKind,
    searchView,
  ]);

  useEffect(() => {
    const canvas = resultsCanvasRef.current;
    if (!canvas) return undefined;
    const frame = window.requestAnimationFrame(() => {
      canvas.scrollTop = getScrollPosition(resultsCanvasKey);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      setScrollPosition(resultsCanvasKey, canvas.scrollTop);
    };
  }, [getScrollPosition, resultsCanvasKey, setScrollPosition]);

  const jumpToAgentResults = useCallback((turnId: string, resultSetId?: string | null) => {
    window.requestAnimationFrame(() => {
      if (!isActiveRef.current) return;
      agentResultsCanvasRef.current?.jumpTo(turnId, resultSetId);
    });
  }, []);

  const detailCanvasResults = useMemo(() => {
    if (isAgenticMode) return allAgenticResults;
    if (manualCanvasKind === 'browse') return browseGallery.results;
    return filteredResults;
  }, [
    allAgenticResults,
    browseGallery.results,
    filteredResults,
    isAgenticMode,
    manualCanvasKind,
  ]);
  useEffect(() => {
    if (isAgenticMode || !selectedSearchResult) {
      return;
    }

    const stillVisible = isResultRepresentedInCanvas(
      selectedSearchResult,
      detailCanvasResults,
      manualCanvasKind === 'browse'
        ? new Set(browseGallery.videos.map((video) => video.video_id))
        : undefined,
    );
    if (stillVisible) {
      return;
    }

    clearSelectedResultState();
  }, [
    browseGallery.videos,
    clearSelectedResultState,
    detailCanvasResults,
    isAgenticMode,
    manualCanvasKind,
    selectedSearchResult,
  ]);

  const handleFiltersChange = (newFilters: FilterCondition[]) => {
    const normalized = normalizeConditionFilters(
      newFilters,
      {
        availableFields: fieldDefinitions,
        dynamicNestedPrefixes: dynamicNestedFilterPrefixes,
      },
    );
    updateManualState({ filters: normalized, error: null });
  };

  useEffect(() => {
    if (isAgenticLocked) {
      return;
    }
    if (persistedAgenticState.sessionId === agenticStoreState.activeSessionId) {
      return;
    }

    setSearchTabState((prev) => {
      if (prev.agenticSearch.sessionId === agenticStoreState.activeSessionId) {
        return prev;
      }
      return {
        ...prev,
        agenticSearch: {
          ...prev.agenticSearch,
          sessionId: agenticStoreState.activeSessionId,
        },
      };
    });
  }, [agenticStoreState.activeSessionId, isAgenticLocked, persistedAgenticState.sessionId, setSearchTabState]);

  useEffect(() => {
    if (!urlHydrated || searchMode !== 'agentic' || isAgenticLocked) {
      pendingSessionRef.current = null;
      return;
    }

    void loadAgenticSessions();
  }, [isAgenticLocked, loadAgenticSessions, searchMode, urlHydrated]);

  useEffect(() => {
    if (!urlHydrated || searchMode !== 'agentic' || isAgenticLocked) {
      pendingSessionRef.current = null;
      return;
    }

    const pendingSessionId = pendingSessionRef.current;
    if (!pendingSessionId) {
      return;
    }

    pendingSessionRef.current = null;

    const shouldLoadPendingSession = pendingSessionId !== agenticStoreState.activeSessionId
      || !agenticActiveDetail;
    if (!shouldLoadPendingSession) {
      return;
    }

    void openAgenticSession(pendingSessionId).then((loaded) => {
      if (loaded || !isActiveRef.current || locationRef.current !== location) {
        return;
      }
      commitOwnedSearch({
        location,
        navigate,
        ownedKeys: OWNED_KEYS,
        updates: { mode: 'agentic', session: null },
        mode: 'replace',
      });
    });
  }, [
    agenticActiveDetail,
    agenticStoreState.activeSessionId,
    location,
    navigate,
    openAgenticSession,
    searchMode,
    isAgenticLocked,
    urlHydrated,
  ]);

  useEffect(() => {
    if (!urlHydrated || searchMode !== 'agentic' || isAgenticLocked) {
      return;
    }

    if (!agenticStoreState.activeSessionId || agenticActiveDetail || agenticStoreState.isLoadingSession) {
      return;
    }

    void openAgenticSession(agenticStoreState.activeSessionId);
  }, [
    agenticActiveDetail,
    agenticStoreState.activeSessionId,
    agenticStoreState.isLoadingSession,
    openAgenticSession,
    searchMode,
    isAgenticLocked,
    urlHydrated,
  ]);

  useEffect(() => {
    if (!isActive || !urlHydrated) return;
    const pendingDetailId = pendingDetailRef.current;
    if (!pendingDetailId) {
      pendingDetailRef.current = null;
      return;
    }

    const parsedDetail = decodeJsonFromBase64Url<SearchDetailTokenV1>(pendingDetailId);
    const hasParsedDetail =
      Boolean(parsedDetail) &&
      (
        (typeof parsedDetail?.result_id === 'string' && parsedDetail.result_id.length > 0)
        || (typeof parsedDetail?.segment_id === 'string' && parsedDetail.segment_id.length > 0)
      );

    // Helper to find a matching result in a list
    const findMatchingResult = (results: SearchResult[]): SearchResult | undefined => {
      return results.find((item: SearchResult) => {
        if (!hasParsedDetail) {
          return item.result_id === pendingDetailId || item.segment_id === pendingDetailId;
        }
        if (parsedDetail?.result_id) {
          return item.result_id === parsedDetail.result_id;
        }
        if (item.segment_id !== parsedDetail!.segment_id) return false;
        if (parsedDetail!.video_id && item.video_id !== parsedDetail!.video_id) return false;
        const itemRunId = getResultRunId(item);
        if ((parsedDetail!.run_id ?? null) !== (itemRunId ?? null)) return false;
        const itemIndexId = item.source_index_id ?? null;
        if ((parsedDetail!.source_index_id ?? null) !== (itemIndexId ?? null)) return false;
        return true;
      });
    };

    if (isAgenticMode) {
      const agenticMatch = findMatchingResult(allAgenticResults);
      if (agenticMatch) {
        pendingDetailRef.current = null;
        setSelectedSearchResult(agenticMatch);
        return;
      }
      if (agenticStoreState.activeSessionId && (!agenticActiveDetail || agenticStoreState.isLoadingSession)) {
        return;
      }
      pendingDetailRef.current = null;
      return;
    }

    // Non-agentic mode: original logic
    if (searchResults.length === 0) {
      if (isLoading) return;
      if (!hasSearched) {
        pendingDetailRef.current = null;
        commitOwnedSearch({
          location,
          navigate,
          ownedKeys: OWNED_KEYS,
          updates: { mode: searchMode, detail: null },
          mode: 'replace',
        });
        return;
      }
      pendingDetailRef.current = null;
      setUrlNotice('Requested search result is not available.');
      commitOwnedSearch({
        location,
        navigate,
        ownedKeys: OWNED_KEYS,
        updates: { mode: searchMode, detail: null },
        mode: 'replace',
      });
      return;
    }

    const resultMatch = findMatchingResult(searchResults);
    if (resultMatch) {
      pendingDetailRef.current = null;
      setSelectedSearchResult(resultMatch);
      return;
    }

    if (!isLoading && hasSearched) {
      pendingDetailRef.current = null;
      setUrlNotice('Requested search result is stale or no longer available.');
      commitOwnedSearch({
        location,
        navigate,
        ownedKeys: OWNED_KEYS,
        updates: { mode: searchMode, detail: null },
        mode: 'replace',
      });
    }
  }, [
    isActive,
    agenticActiveDetail,
    agenticStoreState.activeSessionId,
    agenticStoreState.isLoadingSession,
    allAgenticResults,
    hasSearched,
    isAgenticMode,
    isLoading,
    location,
    navigate,
    searchMode,
    searchResults,
    setSelectedSearchResult,
    urlHydrated,
  ]);

  useEffect(() => {
    if (!isAgenticMode || !selectedSearchResult) {
      return;
    }

    const stillVisible = isResultRepresentedInCanvas(selectedSearchResult, allAgenticResults);
    if (stillVisible) {
      return;
    }

    setSelectedSearchResult(null);
  }, [allAgenticResults, isAgenticMode, selectedSearchResult, setSelectedSearchResult]);

  const handleSelectAgenticSession = useCallback((sessionId: string) => {
    historyModeRef.current = 'push';
    setSelectedSearchResult(null);
    void openAgenticSession(sessionId);
  }, [openAgenticSession, setSelectedSearchResult]);

  const handleNewAgenticSession = useCallback(() => {
    historyModeRef.current = 'push';
    setSelectedSearchResult(null);
    startAgenticSessionDraft();
  }, [setSelectedSearchResult, startAgenticSessionDraft]);

  useEffect(() => {
    setSelectedAgentOccurrenceKey(null);
  }, [agenticStoreState.activeSessionId]);

  const handleDeleteAgenticSession = useCallback(async (sessionId: string) => {
    historyModeRef.current = 'push';
    if (agenticStoreState.activeSessionId === sessionId) {
      setSelectedSearchResult(null);
    }
    await deleteAgenticSession(sessionId);
  }, [agenticStoreState.activeSessionId, deleteAgenticSession, setSelectedSearchResult]);

  // Listen for search sidebar width changes
  useEffect(() => {
    const handleSidebarWidthChange = (event: CustomEvent) => {
      setSidebarWidth(event.detail.width);
    };

    window.addEventListener('searchSidebarWidthChange' as any, handleSidebarWidthChange);

    return () => {
      window.removeEventListener('searchSidebarWidthChange' as any, handleSidebarWidthChange);
    };
  }, []);

  // Handler for search result card clicks
  const handleResultClick = useCallback((result: SearchResult) => {
    historyModeRef.current = 'push';
    const resultsSource: SearchResult[] = isAgenticMode ? allAgenticResults : searchResults;
    const clickedResultKey = getResultKey(result);
    const enrichedResult = resultsSource.find(
      (candidate: SearchResult) => getResultKey(candidate) === clickedResultKey,
    );
    setSelectedSearchResult(enrichedResult || result);
  }, [allAgenticResults, isAgenticMode, searchResults, setSelectedSearchResult]);

  const handleAgentResultOccurrenceClick = useCallback((
    result: SearchResult,
    occurrenceKey: string,
  ) => {
    historyModeRef.current = 'push';
    setSelectedAgentOccurrenceKey(occurrenceKey);
    // Preserve the exact message/result-set projection that was clicked. The
    // same physical segment may intentionally occur elsewhere in the thread.
    setSelectedSearchResult(result);
  }, [setSelectedSearchResult]);

  const handleCloseSidebar = () => {
    historyModeRef.current = 'push';
    setSelectedAgentOccurrenceKey(null);
    setSelectedSearchResult(null);
  };

  const handleResultMarkerChange = useCallback((result: SearchResult, marker: MarkerInfo) => {
    const targetResultKey = getResultKey(result);

    if (searchMode === 'agentic') {
      patchAgenticActiveResults(
        (candidate) => getResultKey(candidate) === targetResultKey,
        { marker },
      );
      setSearchTabState((prev) => {
        const currentSelected = prev.selectedSearchResult;
        if (!currentSelected || getResultKey(currentSelected) !== targetResultKey) {
          return prev;
        }
        return {
          ...prev,
          selectedSearchResult: {
            ...currentSelected,
            marker,
          },
        };
      });
      return;
    }

    setSearchTabState(prev => {
      const patchResults = (results: SearchResult[]): SearchResult[] =>
        results.map((candidate) => (
          getResultKey(candidate) === targetResultKey ? { ...candidate, marker } : candidate
        ));

      const next: any = { ...prev };
      if (searchMode === 'vector') {
        next.vectorSearch = { ...prev.vectorSearch, searchResults: patchResults(prev.vectorSearch.searchResults) };
      } else if (searchMode === 'sql') {
        next.sqlSearch = { ...prev.sqlSearch, searchResults: patchResults(prev.sqlSearch.searchResults) };
      }

      if (prev.selectedSearchResult && getResultKey(prev.selectedSearchResult) === targetResultKey) {
        next.selectedSearchResult = { ...prev.selectedSearchResult, marker };
      }

      return next;
    });
  }, [patchAgenticActiveResults, searchMode, setSearchTabState]);

  const manualDatasetStatus: DatasetStatus = isSqlMode
    ? sqlDatasetStatus
    : query.trim() || searchImage
      ? vectorDatasetStatus
      : conditionDatasetStatus;
  const isManualDatasetPending = (
    !urlHydrated
    || bootstrapStatus === 'loading'
    || manualDatasetStatus === 'loading'
  );
  const selectedIndexHeading = indexes.find((index) => index.index_id === firstSelectedIndex)?.name
    || (bootstrapStatus === 'error'
      ? 'Index unavailable'
      : !urlHydrated
        ? 'Loading index…'
        : firstSelectedIndex ? 'Index unavailable' : 'Select an index');

  return (
    <div
      className={`search-tab ${selectedSearchResult ? 'sidebar-open' : ''}`}
      style={{ marginRight: selectedSearchResult ? `${sidebarWidth}px` : '0' }}
    >
      <div className="search-workspace-shell">
      <SearchConfigurationPanel mode={isAgenticMode ? 'agent' : 'search'}>
        <div className="search-panel-tabs" role="tablist" aria-label="Search workspace mode">
          <button
            type="button"
            role="tab"
            aria-selected={!isAgenticMode}
            className={`search-panel-tab ${!isAgenticMode ? 'active' : ''}`}
            onClick={() => {
              setSearchPanel('search');
              setSearchMode('vector');
            }}
          >
            <Search size={16} />
            <span>Search</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isAgenticMode}
            className={`search-panel-tab ${isAgenticMode ? 'active' : ''} ${isAgenticLocked ? 'locked' : ''}`}
            onClick={() => {
              if (!isAgenticLocked) {
                activateAgenticWorkspace();
              }
              setSearchPanel('agent');
              setSearchMode('agentic');
            }}
            title={agenticLockedFeature?.reason}
          >
            {isAgenticLocked ? <Lock size={16} /> : <MessageSquare size={16} />}
            <span>Agent</span>
          </button>
        </div>

      <form onSubmit={handleSearch} className="search-form search-configuration-form">
        {!isAgenticMode && (
        <div className={`form-row ${searchMode === 'vector' ? 'vector-search-form-row' : ''}`}>
          {(
            <SearchQueryInput
              inputId="query"
              label="Search videos"
              hint="Describe what appears, happens, or is said"
              value={query}
              onChange={value => updateManualState({ query: value })}
              placeholder={isResolvingSearchImage ? 'Preparing image…' : searchImage ? 'Add text to refine the image search…' : 'A person entering a car, a spoken phrase, an event…'}
              disabled={isLoading || !urlHydrated}
              clearDisabled={isLoading}
              hasAttachment={Boolean(searchImage)}
              controls={<button
                type="button"
                className={`image-upload-btn ${searchImage ? 'has-image' : ''}`}
                onClick={() => imageInputRef.current?.click()}
                disabled={isLoading}
                aria-label={searchImage ? 'Change search image' : 'Add an image to search'}
                title={searchImage ? 'Change search image' : 'Add an image to search'}
              ><Image size={17} /><span>{searchImage ? 'Change' : 'Image'}</span></button>}
            >
              <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
              {searchImage && searchImagePreviewSrc && (
                <div className="search-media-attachments">
                  <div className="search-media-chip" title="Uploaded image">
                    <button
                      type="button"
                      className="search-media-remove-btn"
                      onClick={handleRemoveImage}
                      disabled={isLoading}
                      aria-label="Remove uploaded image"
                    >
                      <X size={10} />
                    </button>
                    <img src={searchImagePreviewSrc} alt="Uploaded search" className="search-media-thumb" />
                  </div>
                </div>
              )}
            </SearchQueryInput>
          )}

          {!urlHydrated ? (
            <div className="search-scope-controls" aria-busy={bootstrapStatus === 'loading'}>
              <span>
                {bootstrapStatus === 'error'
                  ? 'Search workspace unavailable'
                  : firstSelectedIndex
                    ? 'Loading index…'
                    : 'Loading search workspace…'}
              </span>
            </div>
          ) : (
          <SearchScopeControls
            controlIdPrefix="search-scope"
            indexes={indexes}
            runs={getAllSelectedIndexRuns()}
            selectedIndexId={firstSelectedIndex}
            selectedRunIds={selectedRunIds}
            isLoadingIndexes={isLoadingIndexes}
            isBusy={isLoading}
            showIndexDropdown={showIndexDropdown}
            showRunDropdown={showRunDropdown}
            showAllIndexes={showAllIndexes}
            indexSortBy={indexSortBy}
            indexSortAsc={indexSortAsc}
            onIndexDropdownChange={(isOpen) => updateState({ showIndexDropdown: isOpen })}
            onRunDropdownChange={(isOpen) => updateState({ showRunDropdown: isOpen })}
            onShowAllIndexesChange={(showAll) => updateState({ showAllIndexes: showAll })}
            onIndexSortChange={handleSortChange}
            onIndexChange={(indexId) => applySharedWorkspaceScope({ indexIds: [indexId], runIds: null })}
            onRunIdsChange={(runIds) => {
              if (selectedIndexes?.length) {
                applySharedWorkspaceScope({ indexIds: selectedIndexes, runIds });
              }
            }}
            getRunSecondaryText={getSearchScopeRunSecondaryText}
          />
          )}

          {/* Search Fields Selector - only for vector search mode */}
          {searchMode === 'vector' && (
            <div className="form-group flex-1 search-fields-container">
              <label htmlFor="search-fields-trigger" className="form-label">
                Search In
              </label>
              <div className="custom-select-container">
                <button
                  id="search-fields-trigger"
                  type="button"
                  className="custom-select-trigger search-fields-trigger"
                  onClick={() => {
                    if (searchableFieldDefinitions.length === 0) return;
                    updateState({ showIndexDropdown: false, showRunDropdown: false });
                    setShowSearchFieldsDropdown(!showSearchFieldsDropdown);
                  }}
                  disabled={isLoading || searchableFieldDefinitions.length === 0}
                  aria-haspopup="true"
                  aria-expanded={showSearchFieldsDropdown && !isLoading && searchableFieldDefinitions.length > 0}
                >
                  <span className="select-value">
                    {!selectedIndexes || selectedIndexes.length === 0
                      ? 'Select index first'
                      : searchableFieldDefinitions.length === 0
                        ? 'All Fields'
                        : effectiveSearchFields && effectiveSearchFields.length === 0
                          ? 'No fields selected'
                          : !effectiveSearchFields || effectiveSearchFields.length === searchableFieldDefinitions.length
                            ? 'All Fields'
                            : `${effectiveSearchFields.length} of ${searchableFieldDefinitions.length} fields`}
                  </span>
                  {showSearchFieldsDropdown && !isLoading && searchableFieldDefinitions.length > 0
                    ? <ChevronUp size={16} />
                    : <ChevronDown size={16} />}
                </button>

                {showSearchFieldsDropdown && !isLoading && searchableFieldDefinitions.length > 0 && (
                  <div className="custom-dropdown search-fields-dropdown grouped-dropdown">
                    <div className="dropdown-header">
                      <span className="dropdown-title">Select Search Fields</span>
                      <button
                        type="button"
                        className="select-all-btn"
                        onClick={() => {
                          const allSelected = !effectiveSearchFields || effectiveSearchFields.length === searchableFieldDefinitions.length;
                          if (allSelected) {
                            updateState({ searchFields: [] });
                          } else {
                            // Select all
                            updateState({ searchFields: null });
                          }
                        }}
                      >
                        {!effectiveSearchFields || effectiveSearchFields.length === searchableFieldDefinitions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>

                    <div className="dropdown-list search-fields-list">
                      {/* Show hierarchical grouping when multiple indexes/runs */}
                      {searchableGroupedFieldDefinitions.length > 1 || (searchableGroupedFieldDefinitions.length === 1 && searchableGroupedFieldDefinitions[0].runs.length > 1) ? (
                        searchableGroupedFieldDefinitions.map((indexGroup, indexIdx) => (
                          <div key={indexGroup.indexId} className="dropdown-group">
                            {searchableGroupedFieldDefinitions.length > 1 && (
                              <div className="dropdown-group-header index-header">
                                <span className="group-name">{indexGroup.indexName}</span>
                                <span className="group-count">{indexGroup.runs.reduce((acc, r) => acc + r.fields.length, 0)} fields</span>
                              </div>
                            )}
                            {indexGroup.runs.map((runGroup, runIdx) => (
                              <div key={runGroup.runId} className="dropdown-subgroup">
                                {(indexGroup.runs.length > 1 || searchableGroupedFieldDefinitions.length > 1) && (
                                  <div className="dropdown-subgroup-header">
                                    <span className="subgroup-name">{runGroup.runName}</span>
                                    {(() => {
                                      const runsForIndex = indexRuns[indexGroup.indexId] || [];
                                      const runInfo = runsForIndex.find(r => r.run_id === runGroup.runId);
                                      if (runInfo) {
                                        return (
                                          <span className="subgroup-info">
                                            {new Date(runInfo.executed_at).toLocaleDateString()} - {getPromptRunVideoCountLabel(runInfo)}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                )}
                                {runGroup.fields.map((field) => {
                                  const isSelected = !effectiveSearchFields || effectiveSearchFields.includes(field.name);
                                  return (
                                    <label
                                      key={`${indexGroup.indexId}-${runGroup.runId}-${field.name}`}
                                      className={`dropdown-item search-field-item ${isSelected ? 'selected' : ''} ${(indexGroup.runs.length > 1 || searchableGroupedFieldDefinitions.length > 1) ? 'grouped-item nested-item' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {
                                          if (!effectiveSearchFields) {
                                            const newFields = searchableFieldDefinitions.map(f => f.name).filter(n => n !== field.name);
                                            updateState({ searchFields: newFields });
                                          } else if (isSelected) {
                                            const newFields = effectiveSearchFields.filter(f => f !== field.name);
                                            updateState({ searchFields: newFields });
                                          } else {
                                            const newFields = [...effectiveSearchFields, field.name];
                                            if (newFields.length === searchableFieldDefinitions.length) {
                                              updateState({ searchFields: null });
                                            } else {
                                              updateState({ searchFields: newFields });
                                            }
                                          }
                                        }}
                                      />
                                      <span className="field-name">{field.label || field.name}</span>
                                      <span className="field-type">{field.type}</span>
                                    </label>
                                  );
                                })}
                                {runIdx < indexGroup.runs.length - 1 && indexGroup.runs.length > 1 && (
                                  <div className="dropdown-subgroup-separator" />
                                )}
                              </div>
                            ))}

                          </div>
                        ))
                      ) : (
                        /* Simple flat list when single index with single run */
                        searchableFieldDefinitions.map((field) => {
                          const isSelected = !effectiveSearchFields || effectiveSearchFields.includes(field.name);
                          return (
                            <label
                              key={field.name}
                              className={`dropdown-item search-field-item ${isSelected ? 'selected' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  if (!effectiveSearchFields) {
                                    // Currently "all fields" - switching to explicit list minus this field
                                    const newFields = searchableFieldDefinitions.map(f => f.name).filter(n => n !== field.name);
                                    updateState({ searchFields: newFields });
                                  } else if (isSelected) {
                                    // Remove this field
                                    const newFields = effectiveSearchFields.filter(f => f !== field.name);
                                    updateState({ searchFields: newFields });
                                  } else {
                                    // Add this field
                                    const newFields = [...effectiveSearchFields, field.name];
                                    // If all fields are now selected, reset to null
                                    if (newFields.length === searchableFieldDefinitions.length) {
                                      updateState({ searchFields: null });
                                    } else {
                                      updateState({ searchFields: newFields });
                                    }
                                  }
                                }}
                              />
                              <span className="field-name">{field.label || field.name}</span>
                              <span className="field-type">{field.type}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
        )}

        {/* Search button row with inline filters */}
        {!isAgenticMode && (
          <div className={`search-action-row ${filters.length > 0 ? 'has-filters' : ''} ${searchMode !== 'vector' || filters.length >= MAX_CONDITIONS ? 'single-secondary-action' : ''}`}>
            {/* Search button - leftmost */}
            {(() => {
              const noRunsSelected = Boolean(selectedRunIds && selectedRunIds.length === 0);
              const noFieldsSelected = searchableFieldDefinitions.length > 0
                && effectiveSearchFields
                && effectiveSearchFields.length === 0;
              const selectionMissing = noRunsSelected || noFieldsSelected;
              const conditionInvalid = effectiveFilters.length > 0
                && !conditionValidation.ok;
              const conditionLikeRunsReady = !isConditionLikeMode || selectedConditionLikeRunsLoaded;
              const isDisabled = isLoading
                || !urlHydrated
                || isResolvingSearchImage
                || selectionMissing
                || conditionInvalid
                || (!query.trim() && !searchImage && effectiveFilters.length === 0)
                || !selectedIndexes
                || selectedIndexes.length === 0
                || !conditionLikeRunsReady;
              const tooltipText = selectionMissing
                ? 'At least one extraction run and field must be selected'
                : conditionInvalid
                  ? conditionValidation.message
                  : undefined;
              return (
                <button
                  type="submit"
                  disabled={isDisabled}
                  className="search-button"
                  title={tooltipText}
                >
                  <Search size={15} />
                  <span>
                    {isLoading
                      ? 'Searching...'
                      : isResolvingSearchImage
                        ? 'Loading image...'
                        : query.trim() && searchImage
                          ? 'Search text + image'
                          : searchImage
                            ? 'Search image'
                            : 'Search'}
                  </span>
                </button>
              );
            })()}

            <button
              type="button"
              className="add-filter-btn clear-all-search-btn"
              onClick={handleClearAllSearch}
              title="Clear query, filters, and results while keeping the current scope"
            >
              <span>Clear Search</span>
            </button>

            {/* Add filter button and filters */}
            {searchMode === 'vector' && (
              <>
                {filters.length < MAX_CONDITIONS && (
                  <button
                    type="button"
                    className="add-filter-btn"
                    onClick={() => {
                      const newFilters = [...filters, { ...DEFAULT_CONDITION_FILTER }];
                      handleFiltersChange(newFilters);
                    }}
                    disabled={isLoading || !selectedIndexes || selectedIndexes.length === 0}
                    aria-label="Add filter"
                    title="Add filter"
                  >
                    <Plus size={16} />
                    <span>Filter</span>
                  </button>
                )}

                <SearchConditionEditor filters={filters} fieldDefinitions={fieldDefinitions} onChange={handleFiltersChange} disabled={isLoading || !selectedIndexes?.length} />
              </>
            )}
          </div>
        )}

        {searchMode !== 'sql' && showPromptDetails && ((selectedRunIds && selectedRunIds.length > 0) || getAllSelectedIndexRuns().length === 1) && (() => {
          const allRuns = getAllSelectedIndexRuns();
          const run = selectedRunIds && selectedRunIds.length > 0
            ? allRuns.find(r => r.run_id === selectedRunIds[0])
            : allRuns.length === 1
              ? allRuns[0]
              : null;
          const prompt = run?.prompt_id ? runPrompts[run.prompt_id] : null;
          return prompt ? (
            <div className="prompt-details-box">
              <div className="prompt-details-header">
                <h4>{prompt.name}</h4>
                {prompt.description && (
                  <p className="prompt-description">{prompt.description}</p>
                )}
            </div>

            <div className="prompt-details-content">
              <div className="prompt-section">
                <h5>Basic Information</h5>
                <div className="prompt-info-grid">
                  <div className="info-item">
                    <span className="info-label">Extraction Engine ID</span>
                    <span className="info-value">{prompt.prompt_id}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">User ID</span>
                    <span className="info-value">{prompt.user_id}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Created At</span>
                    <span className="info-value">{new Date(prompt.created_at).toLocaleString()}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Active Status</span>
                    <span className={`info-value status-badge ${prompt.is_active ? 'active' : 'inactive'}`}>
                      {prompt.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="prompt-section">
                <h5>Extraction Engine Text</h5>
                <div className="prompt-text-container">
                  <p className="prompt-text">{prompt.prompt_text}</p>
                </div>
              </div>

              <div className="prompt-section">
                <h5>Extracted Fields</h5>
                <div className="schema-fields">
                  {(() => {
                    const schemaFields = getPromptSchemaDisplayFields(prompt.json_schema);
                    if (schemaFields.length === 0) {
                      return <p className="no-fields">No fields defined</p>;
                    }

                    return schemaFields.map((field) => (
                      <div key={field.name} className="schema-field">
                        <span className="field-name">{field.name}</span>
                        <span className="field-type">{field.typeLabel}</span>
                        {field.description && (
                          <span className="field-description">{field.description}</span>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
          ) : null;
        })()}
      </form>

      {isAgenticMode && (
        <div className="search-agent-scope">
          {!urlHydrated ? (
            <div className="search-scope-controls" aria-busy={bootstrapStatus === 'loading'}>
              <span>{bootstrapStatus === 'error' ? 'Search workspace unavailable' : 'Loading search workspace…'}</span>
            </div>
          ) : (
          <SearchScopeControls
            controlIdPrefix="agent-scope"
            indexes={indexes}
            runs={getAllSelectedIndexRuns()}
            selectedIndexId={firstSelectedIndex}
            selectedRunIds={selectedRunIds}
            isLoadingIndexes={isLoadingIndexes}
            showIndexDropdown={showIndexDropdown}
            showRunDropdown={showRunDropdown}
            showAllIndexes={showAllIndexes}
            indexSortBy={indexSortBy}
            indexSortAsc={indexSortAsc}
            onIndexDropdownChange={(isOpen) => updateState({ showIndexDropdown: isOpen })}
            onRunDropdownChange={(isOpen) => updateState({ showRunDropdown: isOpen })}
            onShowAllIndexesChange={(showAll) => updateState({ showAllIndexes: showAll })}
            onIndexSortChange={handleSortChange}
            onIndexChange={(indexId) => applySharedWorkspaceScope({ indexIds: [indexId], runIds: null })}
            onRunIdsChange={(runIds) => {
              if (selectedIndexes?.length) {
                applySharedWorkspaceScope({ indexIds: selectedIndexes, runIds });
              }
            }}
            getRunSecondaryText={getSearchScopeRunSecondaryText}
          />
          )}
        </div>
      )}

      {!isAgenticMode && effectiveError && (
        <div className="error-message">
          {effectiveError}
        </div>
      )}

      {urlNotice && (
        <div className="error-message">
          {urlNotice}
          <button onClick={() => setUrlNotice(null)}>×</button>
        </div>
      )}

      {searchMode === 'agentic' && isAgenticLocked && agenticLockedFeature && (
        <LockedFeaturePanel feature={agenticLockedFeature} />
      )}

      {searchMode === 'agentic' && !isAgenticLocked && (
        <AgenticChatThread
          sessionId={agenticStoreState.activeSessionId}
          sessions={userSessions}
          messages={agenticDisplayMessages}
          liveTurn={activeAgenticLiveTurn}
          processingSessionId={agenticProcessingSessionId}
          isLoadingSessions={isLoadingSessions}
          isLoadingSession={agenticStoreState.isLoadingSession}
          isProcessing={isAgenticProcessing}
          statusMessage={activeAgenticLiveTurn ? agenticStatusMessage : ''}
          error={agenticStoreState.error}
          selectedIndexes={selectedIndexes}
          selectedRunIds={selectedRunIds}
          onSelectSession={handleSelectAgenticSession}
          onDeleteSession={handleDeleteAgenticSession}
          onNewSession={handleNewAgenticSession}
          onSendMessage={sendAgenticMessage}
          onSelectResultSet={selectAgenticResultSet}
          onJumpToResults={jumpToAgentResults}
          onResultClick={handleResultClick}
          conversationScrollKey={`agent-conversation::${agenticStoreState.activeSessionId || 'draft'}`}
          getConversationScrollPosition={getScrollPosition}
          setConversationScrollPosition={setScrollPosition}
        />
      )}

      </SearchConfigurationPanel>

      <main className="search-results-canvas" ref={resultsCanvasRef}>
        <header className="search-results-canvas-header">
          <div>
            <span className="search-results-eyebrow">{isAgenticMode ? 'Conversation evidence' : manualCanvasKind === 'browse' ? 'Index gallery' : 'Search results'}</span>
            <h2>{selectedIndexHeading}</h2>
          </div>
          <div className="search-results-canvas-controls">
            <SearchTileSizeControl />
            <div className="search-view-toggle" role="group" aria-label="Result view">
              <button
                type="button"
                className={searchView === 'segments' ? 'active' : ''}
                onClick={() => setSearchView('segments')}
              >
                <LayoutGrid size={15} /> Segments
              </button>
              <button
                type="button"
                className={searchView === 'videos' ? 'active' : ''}
                onClick={() => setSearchView('videos')}
              >
                <Film size={15} /> Videos
              </button>
            </div>
          </div>
        </header>

        {isAgenticMode && !isAgenticLocked && (
          <AgentResultsCanvas
            ref={agentResultsCanvasRef}
            messages={agenticDisplayMessages}
            runs={selectedGalleryRuns}
            view={searchView}
            onResultClick={handleResultClick}
            onResultOccurrenceClick={handleAgentResultOccurrenceClick}
            onMarkerChange={handleResultMarkerChange}
            selectedResultKey={selectedSearchResultKey}
            selectedOccurrenceKey={selectedAgentOccurrenceKey}
          />
        )}

        {!isAgenticMode && manualCanvasKind === 'browse' && (
          <>
            {browseGallery.error && (
              <div className="error-message search-gallery-error" role="alert">
                <span>{browseGallery.error}</span>
                <button type="button" onClick={browseGallery.retryGallery}>Retry gallery</button>
              </div>
            )}
            {selectedRunCatalogStatus === 'error' ? (
              <div className="error-message search-gallery-error" role="alert">
                <span>Unable to load searchable runs for this index.</span>
                <button type="button" onClick={retrySelectedRunCatalog}>Retry</button>
              </div>
            ) : (
            <SearchResultsGallery
              results={browseGallery.results}
              videos={browseGallery.videos}
              runs={selectedGalleryRuns}
              view={searchView}
              onResultClick={handleResultClick}
              onMarkerChange={handleResultMarkerChange}
              selectedResultKey={selectedSearchResultKey}
              includeEmptyVideoRows
              hasMoreByTarget={browseGallery.hasMoreByTarget}
              loadingTargetKey={browseGallery.loadingTargetKey}
              onLoadMoreBand={browseGallery.loadMoreBand}
              onEnsureVideoBands={browseGallery.ensureVideoBands}
              emptyMessage={selectedGalleryRuns.length === 0 ? 'Select at least one searchable run.' : 'No processed videos belong to the selected runs.'}
              isPending={
                !urlHydrated
                || !selectedConditionLikeRunsLoaded
                || selectedRunCatalogStatus === 'loading'
                || bootstrapStatus === 'loading'
                || browseGallery.status === 'loading'
              }
            />
            )}
            {selectedRunCatalogStatus !== 'error' && browseGallery.hasMoreVideos && browseGallery.videos.length > 0 && (
              <div className="load-more-container">
                <div ref={browsePaginationSentinelRef} className="search-gallery-pagination-sentinel" aria-hidden="true" />
                <button
                  type="button"
                  className="load-more-btn"
                  onClick={browseGallery.loadMoreVideos}
                  disabled={browseGallery.isLoadingMoreVideos}
                >
                  {browseGallery.isLoadingMoreVideos ? 'Loading…' : 'Load more videos'}
                </button>
              </div>
            )}
          </>
        )}

        {!isAgenticMode && manualCanvasKind === 'search' && (
          <div className="results-section unified-search-results">
            {!isManualDatasetPending && (
              <div className="search-candidate-summary">
                {query.trim() || searchImage
                  ? `${filteredResults.length} matches from ${searchResults.length} ranked candidates (up to 100)`
                  : `${filteredResults.length} conditional matches`}
              </div>
            )}
            <SearchResultsGallery
              results={filteredResults}
              runs={selectedGalleryRuns}
              view={searchView}
              onResultClick={handleResultClick}
              onMarkerChange={handleResultMarkerChange}
              selectedResultKey={selectedSearchResultKey}
              isVectorSearchTile={Boolean(query.trim() || searchImage)}
              prioritizedFieldPaths={prioritizedFilterFieldPaths}
              emptyMessage={query.trim() || searchImage ? 'No match was found within the ranked candidates.' : 'No results match these conditions.'}
              isPending={isManualDatasetPending}
            />
            {!isManualDatasetPending && nextPageToken && (
              <div className="load-more-container">
                <button
                  type="button"
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading…' : 'Load more results'}
                </button>
              </div>
            )}
          </div>
        )}

      </main>
      </div>

      {/* Search Result Detail Sidebar */}
      <SearchResultDetailModal
        result={selectedSearchResult ? detailCanvasResults.find((result) => (
          getSearchResultIdentity(result) === getSearchResultIdentity(selectedSearchResult)
        )) || selectedSearchResult : null}
        isOpen={selectedSearchResult !== null}
        onClose={handleCloseSidebar}
        indexes={indexes}
        indexRuns={indexRuns}
        allSearchResults={detailCanvasResults}
        fullVideoState={selectedResultFullVideoState}
        onFullVideoStateChange={setSelectedResultFullVideoState}
        prioritizedFieldPaths={!isAgenticMode && manualCanvasKind === 'search'
          ? prioritizedFilterFieldPaths
          : undefined}
      />
    </div>
  );
};

export default SearchTab;
